import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  ArrowLeft,
  FolderKanban,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  Link2,
  Loader2,
  AlertCircle,
  Send,
  MessageCircle,
  Paperclip,
  Trash2,
  X,
  Eye,
  Brain,
  PenLine,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ClarificationPayload, Project, STAGE_NAMES, STAGE_MARKS, ProjectArtifact, TopicCandidate } from '../../types/project';
import { projectService } from '../../services/projectService';
import { sanitizeClarificationMessageContent } from '../../utils/clarification';
import { renderContent } from '../../utils/mathRenderer';
import { baseSubjectNameFromId } from '../../utils/subjectDisplay';

type ProjectViewProps = {
  projectId: string;
  onBack: () => void;
  onExitToChat?: () => void;
  onStartProjectChat?: (starterPrompt: string) => void;
};

// Chat message type for local state
type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: Record<string, unknown>;
  topicCandidates?: TopicCandidate[];
  clarification?: ClarificationPayload;
  formFields?: Stage1FormField[];
  showStage2Form?: boolean;
  showDraftReview?: boolean;
  showDraftChoice?: boolean;
  showStageAdvanceConsent?: boolean;
  stageAdvanceTarget?: number;
  draftChoiceStage?: number;
  draftArtifact?: ProjectArtifact | null;
  nextActions?: NextActionItem[];
};

type Stage1FieldKey = 'where' | 'what' | 'who_affected' | 'evidence' | 'constraints';
type TopicConstraintFieldKey = Stage1FieldKey;
type StageFormFieldKey = Stage1FieldKey;

type Stage1FormField = {
  field: StageFormFieldKey;
  label: string;
  placeholder?: string;
  inputType?: 'text' | 'textarea';
};

type NextActionItem = {
  step_number: number;
  action: string;
  who: 'learner' | 'assistant';
  evidence_needed: string;
  due_hint: 'now' | 'today' | 'this week';
};

type FullDraftSection = {
  stage?: number;
  stage_name?: string;
  artifact_type?: string;
  body: string;
};

type Stage2QuickRow = {
  idea: string;
  how: string;
  suitability: string;
  safety: string;
};

type ProjectMessageMedia = {
  mediaId: string;
  fileType: 'AUDIO' | 'IMAGE' | 'VIDEO' | 'FILE' | 'DOCUMENT';
  fileName?: string;
  filePath?: string;
  mimeType?: string;
  fileSize?: number;
  duration?: number;
};

const ARTIFACT_TYPE_ALIASES: Record<string, string> = {
  stage2_search_plan: 'search_plan',
  stage4_selection_draft: 'stage4_development_plan',
};

const getArtifactType = (artifact?: Pick<ProjectArtifact, 'artifact_type'> | null): string =>
  ARTIFACT_TYPE_ALIASES[String(artifact?.artifact_type || '').trim().toLowerCase()] || String(artifact?.artifact_type || '').trim().toLowerCase();

const getProjectArtifacts = (project?: Project | null): ProjectArtifact[] =>
  project?.workspace_artifacts ?? project?.recent_artifacts ?? [];

const getWorkspaceVersionTimestamps = (project?: Project | null): { stage: number; freshness: number } => {
  const version = String(project?.workspace_version || '').trim();
  const parts = version ? version.split('|') : [];
  const stage = Number(parts[0] || project?.current_stage || 0) || Number(project?.current_stage || 0) || 0;
  const freshness = parts.slice(2)
    .map((value) => Date.parse(String(value || '').trim()) || 0)
    .reduce((maxValue, value) => Math.max(maxValue, value), 0);
  return { stage, freshness };
};

const shouldReplaceProjectSnapshot = (current: Project | null, incoming?: Project | null): incoming is Project => {
  if (!incoming) return false;
  if (!current) return true;

  const currentVersion = String(current.workspace_version || '').trim();
  const incomingVersion = String(incoming.workspace_version || '').trim();
  if (currentVersion && incomingVersion) {
    if (currentVersion === incomingVersion) return false;
    const currentMeta = getWorkspaceVersionTimestamps(current);
    const incomingMeta = getWorkspaceVersionTimestamps(incoming);
    if (incomingMeta.stage !== currentMeta.stage) {
      return incomingMeta.stage >= currentMeta.stage;
    }
    if (incomingMeta.freshness !== currentMeta.freshness) {
      return incomingMeta.freshness >= currentMeta.freshness;
    }
  }

  const currentUpdatedAt = Date.parse(String(current.updated_at || current.last_activity_at || '')) || 0;
  const incomingUpdatedAt = Date.parse(String(incoming.updated_at || incoming.last_activity_at || '')) || 0;
  if (currentUpdatedAt && incomingUpdatedAt && incomingUpdatedAt !== currentUpdatedAt) {
    return incomingUpdatedAt >= currentUpdatedAt;
  }

  return Number(incoming.current_stage || 0) >= Number(current.current_stage || 0);
};

const getStageAdvanceTarget = (content: string): number | undefined => {
  const text = String(content || '');
  const moveMatch = text.match(/\bmove(?:\s+you)?\s+to\s+stage\s+(\d+)\b/i);
  if (moveMatch) {
    const stage = Number(moveMatch[1]);
    return Number.isFinite(stage) && stage > 0 ? stage : undefined;
  }
  const continueMatch = text.match(/\bcontinue\s+to\s+stage\s+(\d+)\b/i);
  if (continueMatch) {
    const stage = Number(continueMatch[1]);
    return Number.isFinite(stage) && stage > 0 ? stage : undefined;
  }
  return undefined;
};

const isStageAdvanceConsentPrompt = (content: string): boolean => {
  const text = String(content || '').trim().toLowerCase();
  if (!text) return false;
  if (!text.includes('?')) return false;
  if (text.includes('should i move you to stage')) return true;
  if (text.includes('if this looks correct, should i move you to stage')) return true;
  if (text.includes('would you like me to finalize stage 1 and move to stage 2')) return true;
  if (text.includes('ready to continue to stage')) return true;
  return false;
};

const PROJECT_UPLOAD_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'video/mp4',
  'video/webm',
  'video/quicktime',
].join(',');

const inferUploadFileType = (file: File): ProjectMessageMedia['fileType'] => {
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'IMAGE';
  if (mime.startsWith('audio/')) return 'AUDIO';
  if (mime.startsWith('video/')) return 'VIDEO';
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('msword') || mime.startsWith('text/')) {
    return 'DOCUMENT';
  }
  return 'FILE';
};

const extractProjectMessageMedia = (metadata?: Record<string, unknown>): ProjectMessageMedia | null => {
  if (!metadata || typeof metadata !== 'object') return null;
  const source = (() => {
    const nested = (metadata as { media?: unknown }).media;
    if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
    if ((metadata as Record<string, unknown>).media_id || (metadata as Record<string, unknown>).file_type) {
      return metadata;
    }
    return null;
  })();
  if (!source) return null;
  const mediaId = String(source.media_id || source.id || '').trim();
  if (!mediaId) return null;
  const fileTypeRaw = String(source.file_type || source.type || '').trim().toUpperCase();
  const allowed: ProjectMessageMedia['fileType'][] = ['AUDIO', 'IMAGE', 'VIDEO', 'FILE', 'DOCUMENT'];
  const fileType = (allowed.includes(fileTypeRaw as ProjectMessageMedia['fileType'])
    ? fileTypeRaw
    : 'FILE') as ProjectMessageMedia['fileType'];
  const fileSizeNum = Number(source.file_size);
  const durationNum = Number(source.duration);
  return {
    mediaId,
    fileType,
    fileName: String(source.original_filename || source.file_name || '').trim() || undefined,
    filePath: String(source.file_path || source.path || '').trim() || undefined,
    mimeType: String(source.mime_type || '').trim() || undefined,
    fileSize: Number.isFinite(fileSizeNum) && fileSizeNum > 0 ? fileSizeNum : undefined,
    duration: Number.isFinite(durationNum) && durationNum >= 0 ? durationNum : undefined,
  };
};

// Thinking indicator bubble - shown while AI is processing
const ThinkingBubble: React.FC = () => (
  <div className="flex justify-start">
    <div className="max-w-[75%] sm:max-w-[70%] rounded-2xl px-4 py-3 bg-[var(--surface-elevated)] border border-[var(--border-color)]">
      <div className="flex items-center gap-3" role="status" aria-label="AI is thinking">
        <span className="sr-only">AI is thinking</span>
        <div className="thinking-brain-container relative flex items-center justify-center">
          <div className="thinking-brain-glow absolute inset-0 rounded-full" />
          <Brain className="thinking-brain-icon h-5 w-5 text-[var(--accent-primary)]" />
        </div>
        <span className="text-xs text-[var(--text-secondary)] font-medium thinking-text">Thinking</span>
      </div>
    </div>
  </div>
);

// Draft choice card - lets student choose between AI draft or self-writing
const DraftChoiceCard: React.FC<{
  stage: number;
  disabled?: boolean;
  onLetAIDraft: () => void;
  onDoItMyself: () => void;
}> = ({ stage, disabled, onLetAIDraft, onDoItMyself }) => {
  const [submitted, setSubmitted] = useState(false);
  const stageName = STAGE_NAMES[stage] || `Stage ${stage}`;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
      <div className="text-xs font-medium text-[var(--text-secondary)] mb-2.5">
        How would you like to approach {stageName}?
      </div>
      {!submitted ? (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => {
              if (disabled) return;
              onLetAIDraft();
              setSubmitted(true);
            }}
            className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 text-xs font-medium hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
            disabled={disabled || submitted}
          >
            <Brain className="w-3.5 h-3.5" />
            Let assistant draft it
          </button>
          <button
            type="button"
            onClick={() => {
              if (disabled) return;
              onDoItMyself();
              setSubmitted(true);
            }}
            className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-200 border border-purple-500/30 text-xs font-medium hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
            disabled={disabled || submitted}
          >
            <PenLine className="w-3.5 h-3.5" />
            I'll write it myself
          </button>
        </div>
      ) : (
        <span className="text-[10px] text-[var(--text-tertiary)]">Choice sent to coach.</span>
      )}
    </div>
  );
};

const StageAdvanceConsentCard: React.FC<{
  nextStage?: number;
  disabled?: boolean;
  onProceed: () => void;
  onSendFeedback: (feedback: string) => void;
}> = ({ nextStage, disabled, onProceed, onSendFeedback }) => {
  const [mode, setMode] = useState<'choices' | 'feedback'>('choices');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const canSendFeedback = !disabled && feedbackText.trim().length > 0;
  const proceedLabel = nextStage && nextStage > 0
    ? `Proceed to Stage ${nextStage}`
    : 'Proceed to next stage';

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
      {!submitted && mode === 'choices' && (
        <div className="space-y-2.5">
          <div className="text-xs font-medium text-[var(--text-secondary)]">
            Confirm if you want me to continue.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (disabled) return;
                onProceed();
                setSubmitted(true);
              }}
              className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 text-xs font-medium hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={disabled}
            >
              {proceedLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                if (disabled) return;
                setMode('feedback');
              }}
              className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={disabled}
            >
              Provide feedback first
            </button>
          </div>
        </div>
      )}
      {!submitted && mode === 'feedback' && (
        <div className="space-y-2.5">
          <div className="text-xs font-medium text-[var(--text-secondary)]">
            Tell me what you want adjusted before I continue.
          </div>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="What should I revise before moving forward?"
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none"
            disabled={disabled}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (!canSendFeedback) return;
                onSendFeedback(feedbackText.trim());
                setSubmitted(true);
              }}
              className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={!canSendFeedback}
            >
              Send feedback
            </button>
            <button
              type="button"
              onClick={() => {
                if (disabled) return;
                setMode('choices');
                setFeedbackText('');
              }}
              className="px-3 py-2 rounded-lg text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors"
              disabled={disabled}
            >
              Back
            </button>
          </div>
        </div>
      )}
      {submitted && (
        <span className="text-[10px] text-[var(--text-tertiary)]">Your response has been sent to the coach.</span>
      )}
    </div>
  );
};

// Stage timeline component
const StageTimeline: React.FC<{
  currentStage: number;
  stageStatus: Record<string, string>;
}> = ({ currentStage, stageStatus }) => {
  return (
    <div className="space-y-2">
      {Object.entries(STAGE_NAMES).map(([stageNum, stageName]) => {
        const num = parseInt(stageNum);
        const status = stageStatus[stageNum] || 'not_started';
        const isCurrent = num === currentStage;
        const isCompleted = status === 'completed';
        const isInProgress = status === 'in_progress' || isCurrent;

        return (
          <div
            key={stageNum}
            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
              isCurrent
                ? 'bg-cyan-500/10 border border-cyan-500/30'
                : isCompleted
                ? 'bg-green-500/10 border border-green-500/20'
                : 'bg-[var(--bg-secondary)] border border-[var(--border-color)]'
            }`}
          >
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
            ) : isInProgress ? (
              <Clock className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-[var(--text-tertiary)] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    isCurrent || isCompleted ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                  }`}
                >
                  Stage {stageNum}: {stageName}
                </span>
                <span className="text-xs text-[var(--text-tertiary)]">{STAGE_MARKS[num]} marks</span>
              </div>
            </div>
            {isCurrent && <ChevronRight className="w-4 h-4 text-cyan-400 flex-shrink-0" />}
          </div>
        );
      })}
    </div>
  );
};

// Topic candidates component
const TopicCandidates: React.FC<{
  candidates: TopicCandidate[];
  onSelectCandidate?: (candidate: TopicCandidate) => void;
}> = ({ candidates, onSelectCandidate }) => {
  if (!candidates || candidates.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-[var(--text-primary)]">Topic suggestions</h4>
      <div className="space-y-2">
        {candidates.map((candidate, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onSelectCandidate?.(candidate)}
            className="w-full text-left p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-cyan-500/40 hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer"
            aria-label={`Select topic candidate: ${candidate.title}`}
          >
            <div className="text-sm font-medium text-[var(--text-primary)]">{candidate.title}</div>
            {candidate.description && (
              <p className="mt-1 text-xs text-[var(--text-secondary)]">{candidate.description}</p>
            )}
            {candidate.syllabus_topic_anchor && (
              <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/20">
                {candidate.syllabus_topic_anchor}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const STAGE1_FIELD_LABELS: Record<Stage1FieldKey, Stage1FormField> = {
  where: {
    field: 'where',
    label: 'What is the exact place (school/community/home) where this problem happens?',
    placeholder: 'e.g. school lab, community market, home kitchen',
    inputType: 'text',
  },
  what: {
    field: 'what',
    label: 'What is the specific problem you are solving with this project (1 sentence, not just "make soap")?',
    placeholder: 'e.g. One sentence explaining the real problem',
    inputType: 'textarea',
  },
  who_affected: {
    field: 'who_affected',
    label: 'Who is most affected by this problem, and how (2-3 short points)?',
    placeholder: 'e.g. learners/households and how they are affected',
    inputType: 'textarea',
  },
  evidence: {
    field: 'evidence',
    label: 'What evidence will you collect this week (photos/interviews/observations/counts)?',
    placeholder: 'e.g. photos, interviews, observations, counts',
    inputType: 'textarea',
  },
  constraints: {
    field: 'constraints',
    label: 'What are your main constraints (time, budget, permissions, safety)?',
    placeholder: 'e.g. time, budget, permissions, safety',
    inputType: 'textarea',
  },
};

const TOPIC_CONSTRAINT_DEFAULT_FIELDS: Record<TopicConstraintFieldKey, Stage1FormField> = {
  where: {
    field: 'where',
    label: 'What is the exact place (school/community/home) where this problem happens?',
    placeholder: 'e.g. school lab, community market, home kitchen',
    inputType: 'text',
  },
  what: {
    field: 'what',
    label: 'What is the specific problem you are solving with this project (1 sentence, not just "make soap")?',
    placeholder: 'e.g. One sentence explaining the real problem',
    inputType: 'textarea',
  },
  who_affected: {
    field: 'who_affected',
    label: 'Who is most affected by this problem, and how (2-3 short points)?',
    placeholder: 'e.g. learners/households and how they are affected',
    inputType: 'textarea',
  },
  evidence: {
    field: 'evidence',
    label: 'What evidence will you collect this week (photos/interviews/observations/counts)?',
    placeholder: 'e.g. photos, interviews, observations, counts',
    inputType: 'textarea',
  },
  constraints: {
    field: 'constraints',
    label: 'What are your main constraints (time, budget, permissions, safety)?',
    placeholder: 'e.g. time, budget, permissions, safety',
    inputType: 'textarea',
  },
};

const TOPIC_CONSTRAINT_FIELD_ORDER: TopicConstraintFieldKey[] = [
  'where',
  'what',
  'who_affected',
  'evidence',
  'constraints',
];

const isBlankPlaceholder = (value: unknown): boolean => {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return true;
  return s.includes('___') || s === '_' || s === 'tbd' || s === 'to be decided';
};

const getLatestStage1DraftPack = (artifacts?: ProjectArtifact[]) => {
  if (!Array.isArray(artifacts)) return undefined;
  const found = artifacts.find(
    (a) => getArtifactType(a) === 'stage1_draft_pack' && a?.stage === 1 && a?.content
  );
  return found?.content && typeof found.content === 'object' ? found.content : undefined;
};

const getPendingTopicConstraintsContent = (artifacts?: ProjectArtifact[]) => {
  if (!Array.isArray(artifacts)) return undefined;
  const topicConstraintArtifacts = artifacts.filter(
    (art) => getArtifactType(art) === 'topic_constraints' && art?.stage === 1 && !!art?.content && typeof art.content !== 'string'
  );
  if (!topicConstraintArtifacts.length) return undefined;
  const latest = [...topicConstraintArtifacts].sort((a, b) => {
    const aTs = Date.parse(String(a?.created_at || '')) || 0;
    const bTs = Date.parse(String(b?.created_at || '')) || 0;
    return bTs - aTs;
  })[0];
  if (!latest || typeof latest.content === 'string') return undefined;
  const content = latest.content as Record<string, unknown>;
  const status = String((content as { status?: unknown }).status || '').trim().toLowerCase();
  const captured = String((content as { captured_from_user?: unknown }).captured_from_user || '').trim();
  if (status && status !== 'pending') return undefined;
  if (captured) return undefined;
  return content;
};

const buildTopicConstraintFormFields = (project: Project | null): Stage1FormField[] | undefined => {
  if (!project) return undefined;
  const content = getPendingTopicConstraintsContent(getProjectArtifacts(project));
  if (!content) return undefined;

  const customFields = (content as { form_fields?: unknown }).form_fields;
  if (Array.isArray(customFields)) {
    const out: Stage1FormField[] = [];
    for (const raw of customFields) {
      if (!raw || typeof raw !== 'object') continue;
      const row = raw as Record<string, unknown>;
      const key = String(row.field || '').trim().toLowerCase() as TopicConstraintFieldKey;
      if (!(key in TOPIC_CONSTRAINT_DEFAULT_FIELDS)) continue;
      const fallback = TOPIC_CONSTRAINT_DEFAULT_FIELDS[key];
      const rawInput = String(row.input_type || row.inputType || fallback.inputType || 'textarea').trim().toLowerCase();
      const inputType: 'text' | 'textarea' = rawInput === 'text' ? 'text' : 'textarea';
      out.push({
        field: key,
        label: String(row.label || fallback.label).trim() || fallback.label,
        placeholder: String(row.placeholder || fallback.placeholder || '').trim() || fallback.placeholder,
        inputType,
      });
    }
    if (out.length) return out;
  }
  return TOPIC_CONSTRAINT_FIELD_ORDER.map((key) => TOPIC_CONSTRAINT_DEFAULT_FIELDS[key]);
};

const getStage1MissingFields = (pack: Record<string, unknown> | undefined): Stage1FieldKey[] => {
  if (!pack) return [];
  const ps = (pack as { problem_statement?: Record<string, unknown> }).problem_statement || {};
  const ds = (pack as { design_specifications?: Record<string, unknown> }).design_specifications || {};
  const missing: Stage1FieldKey[] = [];

  if (isBlankPlaceholder(ps.where)) missing.push('where');
  if (isBlankPlaceholder(ps.what)) missing.push('what');
  if (isBlankPlaceholder(ps.who_affected)) missing.push('who_affected');
  if (isBlankPlaceholder(ps.evidence)) missing.push('evidence');
  if (isBlankPlaceholder(ds.constraints)) missing.push('constraints');

  return missing;
};

const shouldShowStage1Form = (_assistantContent: string, project: Project | null): boolean => {
  if (!project || project.current_stage !== 1) return false;
  return Boolean(buildStage1FormFields(project)?.length);
};

const buildStage1FormFields = (project: Project | null): Stage1FormField[] | undefined => {
  if (!project) return undefined;
  const topicConstraintFields = buildTopicConstraintFormFields(project);
  if (topicConstraintFields?.length) return topicConstraintFields;
  const pack = getLatestStage1DraftPack(getProjectArtifacts(project));
  const missing = getStage1MissingFields(pack);
  if (!missing.length) return undefined;
  return missing.map((field) => STAGE1_FIELD_LABELS[field]);
};

const normalizeNextActions = (raw: unknown): NextActionItem[] => {
  if (!Array.isArray(raw)) return [];
  const out: NextActionItem[] = [];
  for (const step of raw) {
    if (!step || typeof step !== 'object') continue;
    const row = step as Record<string, unknown>;
    const action = String(row.action || '').trim();
    if (!action) continue;

    const parsedStep = Number(row.step_number);
    const stepNumber = Number.isFinite(parsedStep) && parsedStep > 0 ? Math.floor(parsedStep) : out.length + 1;
    const whoRaw = String(row.who || 'learner').trim().toLowerCase();
    const who: 'learner' | 'assistant' = whoRaw === 'assistant' ? 'assistant' : 'learner';
    const evidenceNeeded = String(row.evidence_needed || 'Completion evidence for this step').trim() || 'Completion evidence for this step';
    const dueRaw = String(row.due_hint || 'today').trim().toLowerCase();
    const dueHint: 'now' | 'today' | 'this week' =
      dueRaw === 'now' || dueRaw === 'today' || dueRaw === 'this week' ? dueRaw : 'today';

    out.push({
      step_number: stepNumber,
      action,
      who,
      evidence_needed: evidenceNeeded,
      due_hint: dueHint,
    });
  }
  if (!out.length) return [];
  out.sort((a, b) => a.step_number - b.step_number);
  return out.map((item, idx) => ({ ...item, step_number: idx + 1 }));
};

const getLatestNextActionsForStage = (artifacts: ProjectArtifact[] | undefined, stage: number): NextActionItem[] | undefined => {
  if (!Array.isArray(artifacts) || !artifacts.length) return undefined;
  const stageArtifacts = artifacts.filter((a) => getArtifactType(a) === 'next_actions' && Number(a?.stage) === Number(stage));
  const fallbackArtifacts = artifacts.filter((a) => getArtifactType(a) === 'next_actions');
  const candidate = stageArtifacts[0] || fallbackArtifacts[0];
  if (!candidate) return undefined;
  const actions = normalizeNextActions(candidate.content);
  return actions.length ? actions : undefined;
};

const hasStage2ComparisonFilledArtifact = (artifacts?: ProjectArtifact[]): boolean => {
  if (!Array.isArray(artifacts)) return false;
  return artifacts.some(
    (a) => getArtifactType(a) === 'stage2_comparison_filled' && Number(a?.stage) === 2
  );
};

const isDraftOfferMessage = (content: string): boolean => {
  const lower = content.toLowerCase();
  return (
    lower.includes('i will draft the content for you now') ||
    lower.includes('draft the content for you now?') ||
    lower.includes("i'll draft") ||
    lower.includes('shall i draft') ||
    lower.includes('let me draft') ||
    (lower.includes('moving to') && lower.includes('draft'))
  );
};

const STAGE_WRITEUP_TYPES: Record<number, string[]> = {
  2: ['stage2_investigation_writeup', 'stage2_comparison_filled'],
  3: ['stage3_solutions_draft', 'stage3_option_matrix_draft'],
  4: ['stage4_selection_draft', 'stage4_development_plan', 'stage4_decision_matrix_draft'],
  5: ['stage5_execution_template', 'data_table_template'],
  6: ['stage6_evaluation_draft'],
  7: ['aggregated_project_draft', 'stage7_evaluation_template'],
};

const STAGE_WRITEUP_FALLBACK_KEYWORDS: Record<number, string[]> = {
  2: ['writeup', 'comparison', 'draft'],
  3: ['solution', 'option', 'draft', 'matrix'],
  4: ['selection', 'plan', 'draft', 'decision'],
  5: ['execution', 'template', 'data', 'evidence'],
  6: ['evaluation', 'draft', 'recommendation'],
  7: ['aggregate', 'final', 'compiled', 'draft', 'report'],
};

const getStageWriteupArtifact = (artifacts?: ProjectArtifact[], stage?: number): ProjectArtifact | null => {
  if (!Array.isArray(artifacts)) return null;
  const s = Number(stage || 0);
  const stageArtifacts = artifacts.filter((a) => Number(a?.stage) === s);
  const expected = STAGE_WRITEUP_TYPES[s] || [];

  for (const type of expected) {
    const found = stageArtifacts.find((a) => getArtifactType(a) === type);
    if (found) return found;
  }

  const keywords = STAGE_WRITEUP_FALLBACK_KEYWORDS[s] || ['draft', 'plan'];
  const fallbackByKeyword = stageArtifacts.find((a) => {
    const type = getArtifactType(a);
    if (!type) return false;
    return keywords.some((keyword) => type.includes(keyword));
  });
  if (fallbackByKeyword) return fallbackByKeyword;

  if (s === 2 && stageArtifacts.length > 0) {
    // Stage 2 often starts with a comparison template/matrix before final writeup.
    return stageArtifacts[0];
  }
  return null;
};

const hasStageWriteupArtifact = (artifacts?: ProjectArtifact[], stage?: number): boolean =>
  Boolean(getStageWriteupArtifact(artifacts, stage));

const shouldShowStage2Form = (project: Project | null): boolean => {
  if (!project || Number(project.current_stage) !== 2) return false;
  const artifacts = getProjectArtifacts(project);
  return !hasStage2ComparisonFilledArtifact(artifacts)
    && !hasStageWriteupArtifact(artifacts, 2);
};

const shouldShowDraftReview = (project: Project | null): boolean => {
  if (!project) return false;
  const artifacts = getProjectArtifacts(project);
  const stage = Number(project.current_stage);
  if (stage === 2) {
    return hasStageWriteupArtifact(artifacts, 2)
      || hasStage2ComparisonFilledArtifact(artifacts);
  }
  if ([3, 4, 5, 6].includes(stage)) {
    return hasStageWriteupArtifact(artifacts, stage);
  }
  return false;
};

const hasPlaceholderText = (value: string): boolean => {
  const text = String(value || '').toLowerCase();
  return (
    text.includes('___')
    || text.includes('[draft]')
    || text.includes('to be refined')
    || text.includes('to be filled')
    || text.includes('pending input')
    || /\btbd\b/i.test(text)
  );
};

const contentToDraftBody = (content: unknown): string => {
  if (typeof content === 'string') return content.trim();
  if (!content || typeof content !== 'object') return '';
  const obj = content as Record<string, unknown>;

  if (obj.draft && typeof obj.draft === 'object') {
    const draft = obj.draft as Record<string, unknown>;
    const title = String(draft.proposed_project_title || '').trim();
    const problem = String(draft.problem_statement || '').trim();
    const aim = String(draft.aim_of_project || '').trim();
    const method = String(draft.methodology_and_delimitations || '').trim();
    const terms = Array.isArray(draft.academic_terms_used)
      ? draft.academic_terms_used.map((t) => String(t).trim()).filter(Boolean)
      : [];
    const blocks = [
      '1. Proposed Project Title',
      title || '___',
      '',
      '2. The Problem Statement',
      problem || '___',
      '',
      '3. Aim of the Project',
      aim || '___',
      '',
      '4. Methodology & Delimitations',
      method || '___',
    ];
    if (terms.length) blocks.push('', `Academic terms used: ${terms.join(', ')}`);
    return blocks.join('\n').trim();
  }

  if (obj.problem_statement && typeof obj.problem_statement === 'object') {
    const ps = obj.problem_statement as Record<string, unknown>;
    const ds = obj.design_specifications && typeof obj.design_specifications === 'object'
      ? obj.design_specifications as Record<string, unknown>
      : {};
    const title = String(obj.project_title || obj.title || '').trim();
    const what = String(ps.what || '').trim();
    const where = String(ps.where || '').trim();
    const who = String(ps.who_affected || '').trim();
    const evidence = String(ps.evidence || '').trim();
    const constraints = String(ds.constraints || '').trim();

    const blocks = [
      '1. Proposed Project Title',
      title || '___',
      '',
      '2. The Problem Statement',
      `At ${where || '___'}, ${who || '___'} are affected by ${what || '___'}. Evidence will be gathered through ${evidence || '___'}.`,
      '',
      '3. Aim of the Project',
      `The aim is to investigate ${what || '___'} at ${where || '___'} and develop a practical response.`,
      '',
      '4. Methodology & Delimitations',
      `Evidence collection: ${evidence || '___'}. Constraints: ${constraints || '___'}.`,
    ];
    return blocks.join('\n').trim();
  }

  if (Array.isArray(obj.sections)) {
    const rows = (obj.sections as unknown[])
      .filter((sec) => sec && typeof sec === 'object')
      .map((sec) => {
        const row = sec as Record<string, unknown>;
        const heading = String(row.heading || '').trim();
        const body = String(row.body || '').trim();
        return [heading ? `${heading}\n` : '', body].join('').trim();
      })
      .filter(Boolean);
    if (rows.length) return rows.join('\n\n');
  }

  if (Array.isArray(obj.ideas)) {
    const lines: string[] = [];
    (obj.ideas as unknown[]).forEach((idea, idx) => {
      if (!idea || typeof idea !== 'object') return;
      const row = idea as Record<string, unknown>;
      const name = String(row.name || `Idea ${idx + 1}`).trim();
      const description = String(row.description || '').trim();
      lines.push(`Idea ${idx + 1}: ${name}`);
      if (description) lines.push(description);
      const merits = Array.isArray(row.merits) ? row.merits.map((m) => String(m).trim()).filter(Boolean) : [];
      const demerits = Array.isArray(row.demerits) ? row.demerits.map((d) => String(d).trim()).filter(Boolean) : [];
      if (merits.length) lines.push(`Merits: ${merits.join('; ')}`);
      if (demerits.length) lines.push(`Demerits: ${demerits.join('; ')}`);
      lines.push('');
    });
    const conclusion = String(obj.conclusion || '').trim();
    if (conclusion) lines.push(`Conclusion: ${conclusion}`);
    if (lines.length) return lines.join('\n').trim();
  }

  if (Array.isArray(obj.solutions)) {
    const lines: string[] = [];
    (obj.solutions as unknown[]).forEach((sol, idx) => {
      if (!sol || typeof sol !== 'object') return;
      const row = sol as Record<string, unknown>;
      const title = String(row.title || `Solution ${idx + 1}`).trim();
      const description = String(row.description || '').trim();
      lines.push(`Solution ${idx + 1}: ${title}`);
      if (description) lines.push(description);
      lines.push('');
    });
    if (lines.length) return lines.join('\n').trim();
  }

  if (typeof obj.markdown === 'string' && obj.markdown.trim()) {
    return obj.markdown.trim();
  }

  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return '';
  }
};

const buildFallbackFullDraftSections = (project: Project): FullDraftSection[] => {
  const artifacts = getProjectArtifacts(project);
  const sections: FullDraftSection[] = [];

  const stage1Structured = artifacts.find(
    (a) => getArtifactType(a) === 'stage1_structured_draft' && Number(a?.stage) === 1
  );
  const stage1Pack = artifacts.find(
    (a) => getArtifactType(a) === 'stage1_draft_pack' && Number(a?.stage) === 1
  );
  const stage1Source = stage1Structured || stage1Pack;
  if (stage1Source) {
    sections.push({
      stage: 1,
      stage_name: STAGE_NAMES[1],
      artifact_type: getArtifactType(stage1Source),
      body: contentToDraftBody(stage1Source.content) || '___',
    });
  } else {
    sections.push({ stage: 1, stage_name: STAGE_NAMES[1], artifact_type: 'missing', body: '___' });
  }

  [2, 3, 4, 5, 6, 7].forEach((stage) => {
    const art = getStageWriteupArtifact(artifacts, stage);
    if (art) {
      sections.push({
        stage,
        stage_name: STAGE_NAMES[stage],
        artifact_type: getArtifactType(art),
        body: contentToDraftBody(art.content) || '___',
      });
    } else {
      sections.push({
        stage,
        stage_name: STAGE_NAMES[stage],
        artifact_type: 'missing',
        body: '___',
      });
    }
  });

  return sections;
};

const getFullProjectDraftArtifact = (project: Project | null): ProjectArtifact | null => {
  if (!project) return null;
  const artifacts = getProjectArtifacts(project);
  const fallbackSections = buildFallbackFullDraftSections(project);
  const fallbackMarkdown = [
    `# ${project.title || 'Untitled Project'}`,
    ...fallbackSections.map((sec) => `\n## Stage ${sec.stage || ''}: ${sec.stage_name || ''}\n${sec.body || '___'}`),
  ].join('\n');
  const fallbackMissing = fallbackSections
    .filter((sec) => hasPlaceholderText(sec.body))
    .map((sec) => `Stage ${sec.stage}: ${sec.stage_name}`);

  const explicit = artifacts.find((a) => getArtifactType(a) === 'aggregated_project_draft');
  if (explicit && explicit.content && typeof explicit.content === 'object') {
    const explicitContent = explicit.content as Record<string, unknown>;
    const explicitSectionsRaw = Array.isArray(explicitContent.sections) ? explicitContent.sections : [];
    const explicitSections = explicitSectionsRaw
      .filter((row) => row && typeof row === 'object')
      .map((row) => row as Record<string, unknown>);
    const stage1Section = explicitSections.find((sec) => Number(sec.stage || 0) === 1);
    const stage1Body = String(stage1Section?.body || '').trim();
    const looksLikeOldSlotSnapshot =
      stage1Body.length > 0 &&
      stage1Body.includes('What:') &&
      stage1Body.includes('Where:') &&
      !stage1Body.includes('1. Proposed Project Title');
    if (!looksLikeOldSlotSnapshot) {
      return explicit;
    }
  }

  return {
    id: `aggregated-fallback-${project.id}`,
    stage: Number(project.current_stage || 1),
    artifact_type: 'aggregated_project_draft',
    content: {
      version: 'frontend_fallback_v1',
      title: project.title || 'Untitled Project',
      generated_at: new Date().toISOString(),
      sections: fallbackSections,
      missing_sections: fallbackMissing,
      markdown: fallbackMarkdown,
      integrity_note: 'This fallback draft combines your latest visible stage artifacts.',
    },
  };
};

const normalizeMarkdownCell = (value: string): string => {
  const flat = String(value || '').replace(/\r?\n+/g, ' ').replace(/\|/g, '/').trim();
  return flat || '___';
};

const buildStage2QuickTableMarkdown = (rows: Stage2QuickRow[]): string => {
  if (!Array.isArray(rows)) return '';
  const keptRows = rows
    .map((row) => ({
      idea: String(row.idea || '').trim(),
      how: String(row.how || '').trim(),
      suitability: String(row.suitability || '').trim(),
      safety: String(row.safety || '').trim(),
    }))
    .filter((row) => row.idea || row.how || row.suitability || row.safety);
  if (!keptRows.length) return '';

  const lines: string[] = [
    '| Related idea/approach | How it works (short) | Merits | Demerits | Suitability in my context | Safety/permissions |',
    '| --- | --- | --- | --- | --- | --- |',
  ];
  for (const row of keptRows.slice(0, 3)) {
    lines.push(
      `| ${normalizeMarkdownCell(row.idea)} | ${normalizeMarkdownCell(row.how)} | To be refined with coach | To be refined with coach | ${normalizeMarkdownCell(row.suitability)} | ${normalizeMarkdownCell(row.safety)} |`
    );
  }
  return lines.join('\n');
};

const Stage1FormBubble: React.FC<{
  fields: Stage1FormField[];
  disabled?: boolean;
  onSubmit: (answers: Record<string, string>) => void;
}> = ({ fields, disabled, onSubmit }) => {
  const [values, setValues] = useState<Record<string, string>>(
    fields.reduce((acc, f) => ({ ...acc, [f.field]: '' }), {})
  );
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: StageFormFieldKey, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const cleaned = Object.fromEntries(
    Object.entries(values)
      .map(([k, v]) => [k, v.trim()])
      .filter(([, v]) => v)
  );
  const canSubmit = !disabled && !submitted && Object.keys(cleaned).length > 0;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
      <div className="space-y-3">
        {fields.map((field) => (
          <label key={field.field} className="block">
            <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">
              {field.label}
            </div>
            {field.inputType === 'textarea' ? (
              <textarea
                rows={2}
                value={values[field.field] || ''}
                onChange={(e) => handleChange(field.field, e.target.value)}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50 resize-none"
                disabled={disabled || submitted}
              />
            ) : (
              <input
                type="text"
                value={values[field.field] || ''}
                onChange={(e) => handleChange(field.field, e.target.value)}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50"
                disabled={disabled || submitted}
              />
            )}
          </label>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (!canSubmit) return;
            onSubmit(cleaned);
            setSubmitted(true);
          }}
          className="px-3 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 text-xs font-medium hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canSubmit}
        >
          Submit answers
        </button>
        {submitted && (
          <span className="text-[10px] text-[var(--text-tertiary)]">Sent to tutor.</span>
        )}
      </div>
    </div>
  );
};

const STAGE2_DEFAULT_ROWS: Stage2QuickRow[] = [
  { idea: '', how: '', suitability: '', safety: '' },
  { idea: '', how: '', suitability: '', safety: '' },
  { idea: '', how: '', suitability: '', safety: '' },
];

const Stage2FormBubble: React.FC<{
  disabled?: boolean;
  onSubmit: (rows: Stage2QuickRow[]) => void;
}> = ({ disabled, onSubmit }) => {
  const [rows, setRows] = useState<Stage2QuickRow[]>(STAGE2_DEFAULT_ROWS);
  const [submitted, setSubmitted] = useState(false);

  const handleRowChange = (idx: number, key: keyof Stage2QuickRow, value: string) => {
    setRows((prev) => prev.map((row, rowIdx) => (rowIdx === idx ? { ...row, [key]: value } : row)));
  };

  const cleaned = rows
    .map((row) => ({
      idea: row.idea.trim(),
      how: row.how.trim(),
      suitability: row.suitability.trim(),
      safety: row.safety.trim(),
    }))
    .filter((row) => row.idea || row.how || row.suitability || row.safety);

  const canSubmit = !disabled && !submitted && cleaned.length > 0;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
      <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">
        Quick Stage 2 input (short notes only)
      </div>
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div
            key={`stage2-row-${idx}`}
            className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-2 space-y-2"
          >
            <div className="text-[10px] font-medium text-[var(--text-tertiary)]">Idea {idx + 1}</div>
            <input
              type="text"
              value={row.idea}
              onChange={(e) => handleRowChange(idx, 'idea', e.target.value)}
              placeholder="Related idea/approach"
              className="w-full px-2 py-1.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50"
              disabled={disabled || submitted}
            />
            <input
              type="text"
              value={row.how}
              onChange={(e) => handleRowChange(idx, 'how', e.target.value)}
              placeholder="How it works (short)"
              className="w-full px-2 py-1.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50"
              disabled={disabled || submitted}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={row.suitability}
                onChange={(e) => handleRowChange(idx, 'suitability', e.target.value)}
                placeholder="Suitability in my context"
                className="w-full px-2 py-1.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50"
                disabled={disabled || submitted}
              />
              <input
                type="text"
                value={row.safety}
                onChange={(e) => handleRowChange(idx, 'safety', e.target.value)}
                placeholder="Safety/permissions"
                className="w-full px-2 py-1.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50"
                disabled={disabled || submitted}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (!canSubmit) return;
            onSubmit(cleaned);
            setSubmitted(true);
          }}
          className="px-3 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 text-xs font-medium hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canSubmit}
        >
          Submit Stage 2 notes
        </button>
        {submitted && (
          <span className="text-[10px] text-[var(--text-tertiary)]">Sent to tutor.</span>
        )}
      </div>
    </div>
  );
};

const DraftReviewModal: React.FC<{
  artifact: ProjectArtifact;
  stage: number;
  onClose: () => void;
  onApprove: () => void;
  onEdit: (editRequest: string) => void;
  initialMode?: 'review' | 'editing';
  disabled?: boolean;
}> = ({ artifact, stage, onClose, onApprove, onEdit, initialMode = 'review', disabled }) => {
  const [mode, setMode] = useState<'review' | 'editing'>(initialMode);
  const [editText, setEditText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const stageName = STAGE_NAMES[stage] || `Stage ${stage}`;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    setMode(initialMode);
    setEditText('');
    setSubmitted(false);
  }, [initialMode, artifact.id, stage]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              Stage {stage}: {stageName}
            </h3>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Review the draft below, then approve or request changes
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {typeof artifact.content === 'string' ? (
            <div
              className="text-sm text-[var(--text-primary)] leading-relaxed [&_h1]:text-base [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-medium [&_p]:mb-3 [&_ul]:ml-4 [&_ul]:list-disc [&_ul]:mb-3 [&_ol]:ml-4 [&_ol]:list-decimal [&_ol]:mb-3 [&_li]:mb-1 [&_strong]:text-[var(--text-primary)] [&_em]:text-[var(--text-secondary)]"
              dangerouslySetInnerHTML={{
                __html: renderContent(artifact.content),
              }}
            />
          ) : (
            <div className="[&_.text-xs]:text-sm [&_.text-\\[10px\\]]:text-xs">
              <ArtifactContent artifact={artifact} />
            </div>
          )}
        </div>

        {/* Footer with actions */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]/50">
          {!submitted && mode === 'review' && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  onApprove();
                  setSubmitted(true);
                }}
                className="px-5 py-2.5 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 text-sm font-medium hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={disabled || submitted}
              >
                Approve &amp; continue
              </button>
              <button
                type="button"
                onClick={() => setMode('editing')}
                className="px-5 py-2.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-sm font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={disabled || submitted}
              >
                Request edit
              </button>
            </div>
          )}
          {!submitted && mode === 'editing' && (
            <div className="space-y-3">
              <textarea
                rows={3}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Describe what should be changed (e.g., swap idea 2, add more detail to merits, change the conclusion)..."
                className="w-full px-4 py-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none"
                disabled={disabled}
                autoFocus
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!editText.trim()) return;
                    onEdit(editText.trim());
                    setSubmitted(true);
                  }}
                  className="px-5 py-2.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-sm font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={!editText.trim() || disabled}
                >
                  Send edit request
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('review'); setEditText(''); }}
                  className="px-4 py-2.5 rounded-lg text-[var(--text-tertiary)] text-sm hover:text-[var(--text-secondary)] transition-colors"
                  disabled={disabled}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {submitted && (
            <p className="text-xs text-[var(--text-tertiary)]">
              Your response has been sent to the coach. You can close this window.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const FullProjectDraftModal: React.FC<{
  artifact: ProjectArtifact;
  onClose: () => void;
}> = ({ artifact, onClose }) => {
  const contentObj = artifact.content && typeof artifact.content === 'object'
    ? artifact.content as Record<string, unknown>
    : null;
  const title = contentObj && typeof contentObj.title === 'string' && contentObj.title.trim()
    ? contentObj.title.trim()
    : 'Project Draft';
  const sectionsRaw = contentObj && Array.isArray(contentObj.sections) ? contentObj.sections : [];
  const sections: FullDraftSection[] = sectionsRaw
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const sec = row as Record<string, unknown>;
      return {
        stage: Number(sec.stage || 0) || undefined,
        stage_name: String(sec.stage_name || '').trim() || undefined,
        artifact_type: String(sec.artifact_type || '').trim() || undefined,
        body: String(sec.body || '').trim() || '___',
      };
    });
  const markdown = contentObj && typeof contentObj.markdown === 'string'
    ? contentObj.markdown.trim()
    : '';
  const missingSections = contentObj && Array.isArray(contentObj.missing_sections)
    ? (contentObj.missing_sections as unknown[])
      .map((item) => String(item || '').trim())
      .filter(Boolean)
    : [];

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl">
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">{title}</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Full compiled draft - highlighted sections need your input
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            aria-label="Close full draft modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {missingSections.length > 0 && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
              <p className="text-xs font-medium text-amber-200 mb-1">Sections that still need details</p>
              <p className="text-[11px] text-amber-100/90">{missingSections.join(' • ')}</p>
            </div>
          )}

          {sections.length > 0 ? (
            <div className="space-y-3">
              {sections.map((section, idx) => {
                const needsInput = hasPlaceholderText(section.body);
                const fromInternetSearch = /source attribution \(internet search\)|derived from internet search/i.test(section.body);
                return (
                  <div
                    key={`full-draft-section-${idx}-${section.stage || idx}`}
                    className={`rounded-xl border px-4 py-3 ${
                      needsInput
                        ? 'border-amber-500/40 bg-amber-500/10'
                        : 'border-[var(--border-color)] bg-[var(--surface-elevated)]'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-[var(--text-primary)]">
                        {section.stage ? `Stage ${section.stage}: ` : ''}{section.stage_name || 'Section'}
                      </span>
                      {fromInternetSearch && (
                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-cyan-400/40 text-cyan-200">
                          Internet source-backed
                        </span>
                      )}
                      {needsInput && (
                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-200">
                          Needs input
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">
                      {section.body}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : markdown ? (
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-4 py-3">
              <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">{markdown}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-4 py-3">
              <p className="text-xs text-[var(--text-secondary)]">
                No compiled draft is available yet. Continue chatting and generating stage drafts.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DraftReviewCard: React.FC<{
  stage: number;
  artifact?: ProjectArtifact | null;
  disabled?: boolean;
  onApprove: () => void;
  onEdit: (editRequest: string) => void;
}> = ({ stage, artifact, disabled, onApprove, onEdit }) => {
  const [mode, setMode] = useState<'idle' | 'editing'>('idle');
  const [editText, setEditText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const canSubmit = !disabled && !submitted;
  const stageName = STAGE_NAMES[stage] || `Stage ${stage}`;

  return (
    <>
      <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
        <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">
          Stage {stage} draft is ready for your review
        </div>
        {!submitted && mode === 'idle' && (
          <div className="flex items-center gap-2 flex-wrap">
            {artifact && (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 text-xs font-medium hover:bg-cyan-500/30 transition-colors inline-flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5" />
                Review {stageName} draft
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (!canSubmit) return;
                onApprove();
                setSubmitted(true);
              }}
              className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 text-xs font-medium hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={!canSubmit}
            >
              Approve &amp; continue
            </button>
            <button
              type="button"
              onClick={() => setMode('editing')}
              className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={!canSubmit}
            >
              Request edit
            </button>
          </div>
        )}
        {!submitted && mode === 'editing' && (
          <div className="space-y-2">
            <textarea
              rows={2}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="What should be changed? (e.g., swap idea 2, add more detail to merits)"
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-amber-500/50 resize-none"
              disabled={disabled}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!editText.trim()) return;
                  onEdit(editText.trim());
                  setSubmitted(true);
                }}
                className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!editText.trim() || disabled}
              >
                Send edit request
              </button>
              <button
                type="button"
                onClick={() => { setMode('idle'); setEditText(''); }}
                className="px-3 py-2 rounded-lg text-[var(--text-tertiary)] text-xs hover:text-[var(--text-secondary)]"
                disabled={disabled}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {submitted && (
          <span className="text-[10px] text-[var(--text-tertiary)]">Sent to coach.</span>
        )}
      </div>
      {showModal && artifact && (
        <DraftReviewModal
          artifact={artifact}
          stage={stage}
          onClose={() => setShowModal(false)}
          onApprove={() => {
            if (!canSubmit) return;
            onApprove();
            setSubmitted(true);
            setShowModal(false);
          }}
          onEdit={(text) => {
            onEdit(text);
            setSubmitted(true);
            setShowModal(false);
          }}
          disabled={disabled || submitted}
        />
      )}
    </>
  );
};

const StickyDraftActionsBar: React.FC<{
  stage: number;
  artifact: ProjectArtifact;
  disabled?: boolean;
  onReview: () => void;
  onApprove: () => void;
  onEdit: () => void;
}> = ({ stage, artifact, disabled, onReview, onApprove, onEdit }) => {
  const stageName = STAGE_NAMES[stage] || `Stage ${stage}`;
  const artifactTypeLabel = getArtifactType(artifact).replace(/_/g, ' ').trim();

  return (
    <div className="mb-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-cyan-100">
            Draft ready for Stage {stage}: {stageName}
          </p>
          {artifactTypeLabel && (
            <p className="text-[10px] text-cyan-200/80 truncate">
              {artifactTypeLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={onReview}
            className="px-2.5 py-1.5 rounded-md bg-cyan-500/20 text-cyan-100 border border-cyan-400/30 text-[11px] font-medium hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1"
            disabled={disabled}
          >
            <Eye className="w-3.5 h-3.5" />
            Review
          </button>
          <button
            type="button"
            onClick={onApprove}
            className="px-2.5 py-1.5 rounded-md bg-emerald-500/20 text-emerald-100 border border-emerald-500/30 text-[11px] font-medium hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={disabled}
          >
            Approve
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="px-2.5 py-1.5 rounded-md bg-amber-500/20 text-amber-100 border border-amber-500/30 text-[11px] font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={disabled}
          >
            Request edit
          </button>
        </div>
      </div>
    </div>
  );
};

const ProjectMediaAttachment: React.FC<{ media: ProjectMessageMedia; mediaUrl?: string }> = ({ media, mediaUrl }) => {
  const readableType = media.fileType.toLowerCase();
  const readableSize = media.fileSize ? `${(media.fileSize / (1024 * 1024)).toFixed(1)}MB` : undefined;

  if (media.fileType === 'IMAGE') {
    return (
      <div className="mt-2 rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/30 p-2">
        {mediaUrl ? (
          <img
            src={mediaUrl}
            alt={media.fileName || 'Uploaded image'}
            className="max-h-72 w-full rounded-lg object-contain border border-[var(--border-color)]/50"
          />
        ) : (
          <div className="text-xs text-[var(--text-tertiary)]">Loading image preview…</div>
        )}
        <div className="mt-1 text-[10px] text-[var(--text-tertiary)]">
          {media.fileName || 'Uploaded image'}{readableSize ? ` • ${readableSize}` : ''}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/30 p-2.5">
      <div className="flex items-center gap-2">
        <Paperclip className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-xs text-[var(--text-primary)] truncate">
            {media.fileName || `${readableType} attachment`}
          </div>
          <div className="text-[10px] text-[var(--text-tertiary)]">
            {readableType}{readableSize ? ` • ${readableSize}` : ''}
          </div>
        </div>
      </div>
      {mediaUrl ? (
        <a
          href={mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={media.fileName}
          className="mt-2 inline-flex items-center text-[11px] text-cyan-300 hover:text-cyan-200"
        >
          Open attachment
        </a>
      ) : (
        <div className="mt-2 text-[10px] text-[var(--text-tertiary)]">Preparing attachment…</div>
      )}
    </div>
  );
};

const NextActionsChecklist: React.FC<{ actions: NextActionItem[]; compact?: boolean }> = ({ actions, compact }) => {
  if (!actions.length) return null;
  return (
    <div className={`space-y-2 ${compact ? '' : 'mt-2'}`}>
      {actions.map((step) => (
        <div
          key={`${step.step_number}-${step.action.slice(0, 32)}`}
          className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-cyan-500/20 px-1 text-[10px] font-semibold text-cyan-200">
              {step.step_number}
            </span>
            <span className="text-xs font-medium text-[var(--text-primary)]">{step.action}</span>
          </div>
          <div className="mt-1 text-[10px] text-[var(--text-tertiary)]">
            {step.who} - evidence: {step.evidence_needed} - due: {step.due_hint}
          </div>
        </div>
      ))}
    </div>
  );
};

const CONTENT_META_KEYS = new Set(['version', 'integrity_note', 'artifact_type', 'stage']);

const formatLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StructuredContentDisplay: React.FC<{ data: Record<string, unknown>; depth?: number }> = ({ data, depth = 0 }) => {
  const entries = Object.entries(data).filter(([k]) => !CONTENT_META_KEYS.has(k));
  const integrityNote = typeof data.integrity_note === 'string' ? data.integrity_note.trim() : '';

  if (!entries.length) return null;

  return (
    <div className={`space-y-3 ${depth > 0 ? 'ml-2 pl-2.5 border-l-2 border-[var(--border-color)]/30' : ''}`}>
      {entries.map(([key, value]) => {
        if (value === null || value === undefined) return null;
        const label = formatLabel(key);

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return (
            <div key={key}>
              <div className="text-[10px] font-medium text-[var(--text-primary)] mb-0.5">{label}</div>
              <p className="text-[10px] text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">{String(value)}</p>
            </div>
          );
        }

        if (Array.isArray(value)) {
          const allPrimitive = value.every((v) => typeof v === 'string' || typeof v === 'number');
          if (allPrimitive) {
            return (
              <div key={key}>
                <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">{label}</div>
                <ul className="space-y-0.5 ml-2">
                  {value.map((item, idx) => (
                    <li key={`${key}-${idx}`} className="text-[10px] text-[var(--text-secondary)] flex items-start gap-1.5">
                      <span className="text-cyan-400 mt-px flex-shrink-0">&bull;</span>
                      <span>{String(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          return (
            <div key={key}>
              <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">{label}</div>
              <div className="space-y-2">
                {value.map((item, idx) => {
                  if (item && typeof item === 'object') {
                    return (
                      <div key={`${key}-${idx}`} className="rounded-lg border border-[var(--border-color)]/50 bg-[var(--bg-secondary)]/40 p-2.5">
                        <StructuredContentDisplay data={item as Record<string, unknown>} depth={depth + 1} />
                      </div>
                    );
                  }
                  return <p key={`${key}-${idx}`} className="text-[10px] text-[var(--text-secondary)]">{String(item)}</p>;
                })}
              </div>
            </div>
          );
        }

        if (typeof value === 'object') {
          return (
            <div key={key}>
              <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">{label}</div>
              <div className="rounded-lg border border-[var(--border-color)]/50 bg-[var(--bg-secondary)]/40 p-2.5">
                <StructuredContentDisplay data={value as Record<string, unknown>} depth={depth + 1} />
              </div>
            </div>
          );
        }

        return null;
      })}
      {integrityNote && depth === 0 && (
        <p className="text-[10px] text-[var(--text-tertiary)] italic">{integrityNote}</p>
      )}
    </div>
  );
};

const ArtifactContent: React.FC<{ artifact: ProjectArtifact }> = ({ artifact }) => {
  const artifactType = getArtifactType(artifact);
  const content = artifact.content;
  const actions = normalizeNextActions(content);

  if (artifactType === 'next_actions' && actions.length) {
    return <NextActionsChecklist actions={actions} compact />;
  }

  if (artifactType === 'revision_plan' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const steps = normalizeNextActions(data.steps);
    if (steps.length) {
      return <NextActionsChecklist actions={steps} compact />;
    }
  }

  if (artifactType === 'grading_report' && content && typeof content === 'object') {
    const rubric = (content as Record<string, unknown>).rubric;
    if (rubric && typeof rubric === 'object') {
      return <StructuredContentDisplay data={rubric as Record<string, unknown>} />;
    }
  }

  if (artifactType === 'stage_template' && content && typeof content === 'object') {
    const wrapped = content as Record<string, unknown>;
    const unwrapped = wrapped.content && typeof wrapped.content === 'object'
      ? wrapped.content as Record<string, unknown>
      : wrapped;
    return <StructuredContentDisplay data={unwrapped} />;
  }

  if (artifactType === 'search_plan' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const queries = Array.isArray(data.suggested_queries) ? data.suggested_queries.map((item) => String(item).trim()).filter(Boolean) : [];
    const checks = Array.isArray(data.credibility_checks) ? data.credibility_checks.map((item) => String(item).trim()).filter(Boolean) : [];
    return (
      <div className="space-y-3">
        {queries.length > 0 && (
          <div>
            <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">Suggested queries</div>
            <ul className="space-y-1">
              {queries.map((query, idx) => (
                <li key={`search-plan-${idx}`} className="text-[10px] text-[var(--text-secondary)]">- {query}</li>
              ))}
            </ul>
          </div>
        )}
        {checks.length > 0 && (
          <div>
            <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">Credibility checks</div>
            <ul className="space-y-1">
              {checks.map((item, idx) => (
                <li key={`search-check-${idx}`} className="text-[10px] text-[var(--text-secondary)]">- {item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (artifactType === 'stage2_source_summaries' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const rows = Array.isArray(data.items) ? data.items : Array.isArray(data.summaries) ? data.summaries : [];
    return (
      <div className="space-y-2">
        {rows.map((row, idx) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const title = String(item.source_title || item.title || `Source ${idx + 1}`).trim();
          const body = String(item.summary_draft || item.key_finding || '').trim();
          const why = String(item.why_it_matters || '').trim();
          return (
            <div key={`summary-${idx}`} className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-3">
              <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">{title}</div>
              {body && <p className="text-[10px] text-[var(--text-secondary)] whitespace-pre-wrap">{body}</p>}
              {why && <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">Why it matters: {why}</p>}
            </div>
          );
        })}
      </div>
    );
  }

  if (artifactType === 'stage2_reference_list' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const rows = Array.isArray(data.items) ? data.items : Array.isArray(data.references) ? data.references : [];
    return (
      <div className="space-y-1.5">
        {rows.map((row, idx) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const title = String(item.title || item.citation || `Reference ${idx + 1}`).trim();
          const url = String(item.url || '').trim();
          const meta = [item.author_or_site, item.published_date, item.access_date, item.accessed_on]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(' • ');
          return (
            <div key={`reference-${idx}`} className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-2.5">
              <div className="text-[10px] font-medium text-[var(--text-primary)]">{title}</div>
              {meta && <div className="mt-1 text-[10px] text-[var(--text-tertiary)]">{meta}</div>}
              {url && <div className="mt-1 text-[10px] text-cyan-300 break-all">{url}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  if (artifactType === 'stage2_web_search_results' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const rows = Array.isArray(data.items) ? data.items : [];
    return (
      <div className="space-y-2">
        {rows.map((row, idx) => {
          if (!row || typeof row !== 'object') return null;
          const item = row as Record<string, unknown>;
          const title = String(item.title || `Web source ${idx + 1}`).trim();
          const url = String(item.url || '').trim();
          const snippet = String(item.snippet || '').trim();
          return (
            <div key={`web-result-${idx}`} className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-3">
              <div className="text-[10px] font-medium text-[var(--text-primary)]">{title}</div>
              {url && <div className="mt-1 text-[10px] text-cyan-300 break-all">{url}</div>}
              {snippet && <p className="mt-1 text-[10px] text-[var(--text-secondary)] whitespace-pre-wrap">{snippet}</p>}
            </div>
          );
        })}
      </div>
    );
  }

  if (artifactType === 'data_table_template' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const columns = Array.isArray(data.columns) ? data.columns.map((col) => String(col).trim()).filter(Boolean) : [];
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-medium text-[var(--text-primary)]">Columns</div>
        {columns.map((col, idx) => (
          <div key={`data-col-${idx}`} className="text-[10px] text-[var(--text-secondary)]">- {col}</div>
        ))}
      </div>
    );
  }

  if (artifactType === 'presentation_outline' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const sections = Array.isArray(data.sections) ? data.sections.map((item) => String(item).trim()).filter(Boolean) : [];
    return (
      <div className="space-y-2">
        {sections.map((section, idx) => (
          <div key={`presentation-${idx}`} className="text-[10px] text-[var(--text-secondary)]">
            {idx + 1}. {section}
          </div>
        ))}
      </div>
    );
  }

  if (artifactType === 'stage2_investigation_writeup' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const ideas = Array.isArray(data.ideas) ? data.ideas : [];
    const conclusion = String(data.conclusion || '');
    const integrityNote = String(data.integrity_note || '');

    return (
      <div className="space-y-3">
        {ideas.map((idea, idx) => {
          const i = idea as Record<string, unknown>;
          const name = String(i.name || `Idea ${idx + 1}`);
          const description = String(i.description || '');
          const merits = Array.isArray(i.merits) ? i.merits.map(String) : [];
          const demerits = Array.isArray(i.demerits) ? i.demerits.map(String) : [];
          return (
            <div key={`writeup-idea-${idx}`} className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-3">
              <div className="text-xs font-semibold text-cyan-300 mb-1">Related Idea {idx + 1}: {name}</div>
              {description && (
                <p className="text-xs text-[var(--text-secondary)] mb-2">{description}</p>
              )}
              {merits.length > 0 && (
                <div className="mb-1">
                  <div className="text-[10px] font-medium text-emerald-300">Merits / Advantages</div>
                  <ul className="ml-3 list-disc text-[10px] text-[var(--text-secondary)]">
                    {merits.map((m, mi) => <li key={`merit-${idx}-${mi}`}>{m}</li>)}
                  </ul>
                </div>
              )}
              {demerits.length > 0 && (
                <div>
                  <div className="text-[10px] font-medium text-amber-300">Demerits / Disadvantages</div>
                  <ul className="ml-3 list-disc text-[10px] text-[var(--text-secondary)]">
                    {demerits.map((d, di) => <li key={`demerit-${idx}-${di}`}>{d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
        {conclusion && (
          <div className="rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-3">
            <div className="text-[10px] font-medium text-[var(--text-primary)] mb-1">Conclusion</div>
            <p className="text-xs text-[var(--text-secondary)]">{conclusion}</p>
          </div>
        )}
        {integrityNote && (
          <p className="text-[10px] text-[var(--text-tertiary)] italic">{integrityNote}</p>
        )}
      </div>
    );
  }

  if (artifactType === 'stage2_comparison_filled' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const rawRows = Array.isArray(data.rows) ? data.rows : [];
    const rows = rawRows.filter((row) => row && typeof row === 'object') as Array<Record<string, unknown>>;
    const explicitColumns = Array.isArray(data.columns) ? data.columns.map((col) => String(col)) : [];
    const columns =
      explicitColumns.length > 0
        ? explicitColumns
        : (rows[0] ? Object.keys(rows[0]).map((k) => String(k)) : []);

    if (rows.length > 0 && columns.length > 0) {
      return (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded border border-[var(--border-color)]/60">
            <table className="min-w-full text-[10px] text-[var(--text-secondary)]">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={`stage2-col-${col}`}
                      className="px-2 py-1 text-left font-medium text-[var(--text-primary)] border-b border-[var(--border-color)]/60"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={`stage2-row-${rowIdx}`} className="align-top">
                    {columns.map((col) => (
                      <td
                        key={`stage2-cell-${rowIdx}-${col}`}
                        className="px-2 py-1 whitespace-pre-wrap border-b border-[var(--border-color)]/40"
                      >
                        {String(row[col] ?? '').trim() || '___'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {typeof data.integrity_note === 'string' && data.integrity_note.trim() && (
            <p className="text-[10px] text-[var(--text-tertiary)]">{data.integrity_note.trim()}</p>
          )}
        </div>
      );
    }
  }

  if (artifactType === 'stage2_comparison_template' && content && typeof content === 'object') {
    const rows = content as Record<string, unknown>;
    const columns = Array.isArray(rows.columns) ? rows.columns.map((c) => String(c)) : [];
    return (
      <div className="space-y-1 text-xs text-[var(--text-secondary)]">
        <div className="font-medium text-[var(--text-primary)]">Columns</div>
        {columns.map((col, idx) => (
          <div key={`${col}-${idx}`}>- {col}</div>
        ))}
      </div>
    );
  }

  if (artifactType === 'stage3_solutions_draft' && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const solutions = Array.isArray(data.solutions)
      ? data.solutions
      : Array.isArray(data.options)
      ? data.options
      : [];

    if (solutions.length > 0) {
      const integrityNote = typeof data.integrity_note === 'string' ? data.integrity_note.trim() : '';
      return (
        <div className="space-y-4">
          {solutions.map((sol, idx) => {
            const s = sol as Record<string, unknown>;
            const title = String(s.title || s.name || s.option || `Solution ${idx + 1}`);
            const description = String(s.description || s.how_it_works || s.summary || '');
            const advantages = Array.isArray(s.advantages) ? s.advantages.map(String)
              : Array.isArray(s.merits) ? s.merits.map(String)
              : Array.isArray(s.pros) ? s.pros.map(String) : [];
            const disadvantages = Array.isArray(s.disadvantages) ? s.disadvantages.map(String)
              : Array.isArray(s.demerits) ? s.demerits.map(String)
              : Array.isArray(s.cons) ? s.cons.map(String) : [];
            const costVal = s.cost_resources || s.cost || s.resources;
            const safetyVal = s.safety_note || s.safety;
            const materialsVal = s.materials || s.materials_needed;

            return (
              <div key={`sol-${idx}`} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)]/60 p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-cyan-500/20 text-xs font-bold text-cyan-300 flex-shrink-0">
                    {idx + 1}
                  </span>
                  <h4 className="text-xs font-semibold text-[var(--text-primary)]">{title}</h4>
                </div>
                {description && (
                  <p className="text-[10px] text-[var(--text-secondary)] mb-3 leading-relaxed">{description}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {advantages.length > 0 && (
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2.5">
                      <div className="text-[10px] font-semibold text-emerald-300 mb-1.5 uppercase tracking-wide">Advantages</div>
                      <ul className="space-y-1">
                        {advantages.map((a, ai) => (
                          <li key={`adv-${idx}-${ai}`} className="text-[10px] text-[var(--text-secondary)] flex items-start gap-1.5">
                            <span className="text-emerald-400 mt-px flex-shrink-0">+</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {disadvantages.length > 0 && (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2.5">
                      <div className="text-[10px] font-semibold text-amber-300 mb-1.5 uppercase tracking-wide">Disadvantages</div>
                      <ul className="space-y-1">
                        {disadvantages.map((d, di) => (
                          <li key={`dis-${idx}-${di}`} className="text-[10px] text-[var(--text-secondary)] flex items-start gap-1.5">
                            <span className="text-amber-400 mt-px flex-shrink-0">&ndash;</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {(costVal || safetyVal || materialsVal) ? (
                  <div className="mt-2.5 pt-2.5 border-t border-[var(--border-color)]/40 space-y-1">
                    {costVal ? (
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        <span className="font-medium text-[var(--text-secondary)]">Cost / Resources:</span> {String(costVal)}
                      </p>
                    ) : null}
                    {materialsVal ? (
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        <span className="font-medium text-[var(--text-secondary)]">Materials:</span>{' '}
                        {Array.isArray(materialsVal) ? (materialsVal as string[]).join(', ') : String(materialsVal)}
                      </p>
                    ) : null}
                    {safetyVal ? (
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        <span className="font-medium text-[var(--text-secondary)]">Safety:</span> {String(safetyVal)}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
          {integrityNote && (
            <p className="text-[10px] text-[var(--text-tertiary)] italic">{integrityNote}</p>
          )}
        </div>
      );
    }
  }

  if ((artifactType === 'stage4_development_plan' || artifactType === 'stage4_decision_matrix_draft') && content && typeof content === 'object') {
    const data = content as Record<string, unknown>;
    const sections = Array.isArray(data.sections) ? data.sections : [];
    if (sections.length) {
      return (
        <div className="space-y-3">
          {sections.map((sec, idx) => {
            const row = sec as Record<string, unknown>;
            return (
              <div key={`s4sec-${idx}`} className="rounded-xl border border-[var(--border-color)]/60 bg-[var(--bg-secondary)]/60 p-3">
                <div className="text-[10px] font-semibold text-cyan-300 mb-1 uppercase tracking-wide">
                  {String(row.heading || `Section ${idx + 1}`)}
                </div>
                <div className="text-[10px] text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">
                  {String(row.body || '').trim() || 'To be completed.'}
                </div>
              </div>
            );
          })}
          {typeof data.integrity_note === 'string' && data.integrity_note.trim() && (
            <p className="text-[10px] text-[var(--text-tertiary)] italic">{data.integrity_note.trim()}</p>
          )}
        </div>
      );
    }

    return <StructuredContentDisplay data={data} />;
  }

  if (content && typeof content === 'object') {
    const obj = content as Record<string, unknown>;
    const sections = Array.isArray(obj.sections) ? obj.sections : [];
    if (sections.length) {
      return (
        <div className="space-y-2 text-xs text-[var(--text-secondary)]">
          {sections.map((sec, idx) => {
            const row = sec as Record<string, unknown>;
            return (
              <div key={`${idx}-${String(row.heading || '')}`} className="rounded border border-[var(--border-color)]/60 px-2 py-1">
                <div className="font-medium text-[var(--text-primary)]">{String(row.heading || `Section ${idx + 1}`)}</div>
                <div className="mt-1 whitespace-pre-wrap">
                  {String(row.body || '').trim() || 'Fill this section with your own details.'}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return <StructuredContentDisplay data={obj} />;
  }

  if (typeof content === 'string') {
    return <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">{content}</p>;
  }

  if (content && typeof content === 'object') {
    return <StructuredContentDisplay data={content as Record<string, unknown>} />;
  }

  return (
    <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">
      {String(content ?? '')}
    </p>
  );
};

// Artifacts panel component
const ArtifactsPanel: React.FC<{ artifacts: ProjectArtifact[] }> = ({ artifacts }) => {
  const [expandedArtifactId, setExpandedArtifactId] = useState<string | null>(null);

  if (!artifacts || artifacts.length === 0) {
    return (
      <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
        <p className="text-sm text-[var(--text-secondary)]">No artifacts yet. Start chatting to generate templates and plans.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {artifacts.map((artifact) => (
        <div
          key={artifact.id}
          className="p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[var(--accent-primary)]" />
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {getArtifactType(artifact).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
            <span className="ml-auto text-xs text-[var(--text-tertiary)]">Stage {artifact.stage}</span>
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setExpandedArtifactId((prev) => (prev === artifact.id ? null : artifact.id))}
              className="text-[10px] text-cyan-300 hover:text-cyan-200"
            >
              {expandedArtifactId === artifact.id ? 'Hide details' : 'View details'}
            </button>
          </div>
          {expandedArtifactId === artifact.id && (
            <div className="mt-2 rounded-lg border border-[var(--border-color)]/70 bg-[var(--surface-elevated)] p-2">
              <ArtifactContent artifact={artifact} />
            </div>
          )}
          {artifact.created_at && (
            <div className="mt-1 text-[10px] text-[var(--text-tertiary)]">
              {new Date(artifact.created_at).toLocaleString()}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const ProjectView: React.FC<ProjectViewProps> = ({ projectId, onBack, onExitToChat }) => {
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [mediaObjectUrls, setMediaObjectUrls] = useState<Record<string, string>>({});
  const [streamingContent, setStreamingContent] = useState('');
  const [draftModalState, setDraftModalState] = useState<{
    artifact: ProjectArtifact;
    stage: number;
    initialMode?: 'review' | 'editing';
  } | null>(null);
  const [fullDraftModalArtifact, setFullDraftModalArtifact] = useState<ProjectArtifact | null>(null);
  const [draftUpdatedNotice, setDraftUpdatedNotice] = useState(false);
  const [isClarificationOtherEnabled, setIsClarificationOtherEnabled] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingMediaIdsRef = useRef<Set<string>>(new Set());
  const mediaObjectUrlsRef = useRef<string[]>([]);
  const workspaceVersionRef = useRef<string>('');
  const workspaceRefreshTimersRef = useRef<number[]>([]);
  const draftUpdateTimerRef = useRef<number | null>(null);

  const activeClarificationMessageId = useMemo(() => {
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      const message = chatMessages[i];
      if (message.role !== 'assistant' && message.role !== 'user') {
        continue;
      }
      if (message.role === 'user') {
        return null;
      }
      const options = message.clarification?.options;
      if (Array.isArray(options) && options.length > 0) {
        return message.id;
      }
      return null;
    }
    return null;
  }, [chatMessages]);

  const activeStageConsentMessageId = useMemo(() => {
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      const message = chatMessages[i];
      if (message.role !== 'assistant' && message.role !== 'user') {
        continue;
      }
      if (message.role === 'user') {
        return null;
      }
      if (message.showStageAdvanceConsent) {
        return message.id;
      }
      return null;
    }
    return null;
  }, [chatMessages]);

  useEffect(() => {
    setIsClarificationOtherEnabled(false);
  }, [activeClarificationMessageId]);

  const isStageConsentComposerLocked = Boolean(activeStageConsentMessageId);
  const isClarificationComposerLocked = Boolean(activeClarificationMessageId && !isClarificationOtherEnabled);
  const isComposerLocked = isClarificationComposerLocked || isStageConsentComposerLocked;

  const commitProjectSnapshot = useCallback((incoming?: Project | null) => {
    if (!incoming) return;
    setProject((current) => (shouldReplaceProjectSnapshot(current, incoming) ? incoming : current));
  }, []);

  const loadProject = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      const data = await projectService.getById(projectId);
      if (data) {
        commitProjectSnapshot(data);
      } else if (!silent) {
        setError('Project not found');
      }
    } catch (err) {
      console.error('Failed to load project:', err);
      if (!silent) {
        setError('Failed to load project. Please try again.');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [commitProjectSnapshot, projectId]);

  const loadChatHistory = useCallback(async () => {
    try {
      const messages = await projectService.getMessages(projectId);
      const formattedMessages: ChatMessage[] = messages.map((msg) => {
        const md = msg.metadata && typeof msg.metadata === 'object' ? msg.metadata : undefined;
        const mdTopicCandidates = (md as { topic_candidates?: unknown } | undefined)?.topic_candidates;
        const mdClarification = (md as { clarification?: unknown } | undefined)?.clarification;
        const clarification =
          mdClarification &&
          typeof mdClarification === 'object' &&
          Array.isArray((mdClarification as { options?: unknown }).options)
            ? (mdClarification as ClarificationPayload)
            : undefined;
        const assistantContent = msg.role === 'assistant'
          ? sanitizeClarificationMessageContent(String(msg.content || ''), clarification)
          : String(msg.content || '');
        const showStageAdvanceConsent = msg.role === 'assistant' && isStageAdvanceConsentPrompt(assistantContent);
        const stageAdvanceTarget = showStageAdvanceConsent ? getStageAdvanceTarget(assistantContent) : undefined;
        return {
          id: msg.id,
          role: msg.role as 'user' | 'assistant',
          content: assistantContent,
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          metadata: md as Record<string, unknown> | undefined,
          topicCandidates: Array.isArray(mdTopicCandidates) ? (mdTopicCandidates as TopicCandidate[]) : undefined,
          clarification,
          showStageAdvanceConsent,
          stageAdvanceTarget,
        };
      });
      setChatMessages(formattedMessages);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      // Don't show error - just start with empty chat
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
    loadChatHistory();
  }, [loadProject, loadChatHistory]);

  const scheduleWorkspaceRefresh = useCallback(
    ({ refreshMessages = false }: { refreshMessages?: boolean } = {}) => {
      workspaceRefreshTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      workspaceRefreshTimersRef.current = [700, 1900].map((delay) => window.setTimeout(() => {
        void loadProject({ silent: true });
        if (refreshMessages) {
          void loadChatHistory();
        }
      }, delay));
    },
    [loadChatHistory, loadProject],
  );

  // Auto-scroll to bottom of chat (use scrollTop to avoid scrolling parent containers)
  useEffect(() => {
    const el = messagesAreaRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [chatMessages, streamingContent]);

  // Resolve media_id attachments to object URLs for project messages.
  useEffect(() => {
    const mediaItems = chatMessages
      .map((msg) => extractProjectMessageMedia(msg.metadata))
      .filter((m): m is ProjectMessageMedia => Boolean(m));
    mediaItems.forEach((media) => {
      if (mediaObjectUrls[media.mediaId]) return;
      if (loadingMediaIdsRef.current.has(media.mediaId)) return;
      loadingMediaIdsRef.current.add(media.mediaId);
      void projectService.getMediaObjectUrl(media.mediaId)
        .then((objectUrl) => {
          setMediaObjectUrls((prev) => {
            if (prev[media.mediaId]) {
              URL.revokeObjectURL(objectUrl);
              return prev;
            }
            mediaObjectUrlsRef.current.push(objectUrl);
            return { ...prev, [media.mediaId]: objectUrl };
          });
        })
        .catch((err) => {
          console.warn(`Failed to load media ${media.mediaId}:`, err);
        })
        .finally(() => {
          loadingMediaIdsRef.current.delete(media.mediaId);
        });
    });
  }, [chatMessages, mediaObjectUrls]);

  useEffect(() => {
    return () => {
      mediaObjectUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // Ignore failed revokes.
        }
      });
      mediaObjectUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    return () => {
      workspaceRefreshTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      workspaceRefreshTimersRef.current = [];
      if (draftUpdateTimerRef.current !== null) {
        window.clearTimeout(draftUpdateTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const version = String(project?.workspace_version || '').trim();
    if (!project || !version) return;

    const priorVersion = workspaceVersionRef.current;
    const versionChanged = Boolean(priorVersion) && priorVersion !== version;
    workspaceVersionRef.current = version;

    const latestFullDraft = getFullProjectDraftArtifact(project);
    if (fullDraftModalArtifact && latestFullDraft) {
      setFullDraftModalArtifact((current) => {
        if (!current) return current;
        const currentVersion = String((current.content as { version?: unknown } | undefined)?.version || '');
        const nextVersion = String((latestFullDraft.content as { version?: unknown } | undefined)?.version || '');
        if (current.id === latestFullDraft.id && currentVersion === nextVersion) {
          return current;
        }
        return latestFullDraft;
      });
    }

    if (draftModalState) {
      const liveStage = Number(project.current_stage || draftModalState.stage || 1);
      const liveArtifact = getStageWriteupArtifact(getProjectArtifacts(project), liveStage);
      setDraftModalState((current) => {
        if (!current) return current;
        if (!liveArtifact) {
          return liveStage === current.stage ? current : null;
        }
        if (current.artifact.id === liveArtifact.id && current.stage === liveStage) {
          return current;
        }
        return { ...current, artifact: liveArtifact, stage: liveStage };
      });
    }

    if (versionChanged && latestFullDraft) {
      setDraftUpdatedNotice(true);
      if (draftUpdateTimerRef.current !== null) {
        window.clearTimeout(draftUpdateTimerRef.current);
      }
      draftUpdateTimerRef.current = window.setTimeout(() => setDraftUpdatedNotice(false), 5000);
    }
  }, [draftModalState, fullDraftModalArtifact, project]);

  // Send message using project-specific endpoint
  const handleSendMessage = async (overrideText?: string) => {
    const messageToSend = (overrideText ?? inputText).trim();
    if (!messageToSend || isSending || !project) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsSending(true);
    setStreamingContent('');

    try {
      // Use streaming endpoint for real-time response
      const response = await projectService.chatStream(
        projectId,
        {
          message: userMessage.content,
          allow_web_search: true,
        },
        (chunk, fullContent) => {
          setStreamingContent(fullContent);
        }
      );

      // Add complete assistant message
      const assistantContent = response.answer || streamingContent;
      const responseTopicCandidates = response.project?.topic_candidates;
      const topicNotes = response.project?.topic_selection_notes;
      const responseStage = Number(response.project?.current_stage || project.current_stage || 1);
      const topicChosen = typeof topicNotes === 'string' && topicNotes.includes('topic_chosen:');
      const looksLikeTopicChoiceTurn =
        /which one/i.test(assistantContent) ||
        /\bchoose\b/i.test(assistantContent) ||
        /\bpick\b/i.test(assistantContent) ||
        /\boption\b/i.test(assistantContent);
      const shouldShowCandidates =
        Array.isArray(responseTopicCandidates) &&
        responseTopicCandidates.length > 0 &&
        responseStage === 1 &&
        (!topicChosen || looksLikeTopicChoiceTurn);

      const formFields =
        shouldShowStage1Form(assistantContent, response.project ?? project ?? null)
          ? buildStage1FormFields(response.project ?? project ?? null)
          : undefined;
      const showStage2Form = shouldShowStage2Form(response.project ?? project ?? null);
      const showDraftReview = shouldShowDraftReview(response.project ?? project ?? null);
      const activeStage = responseStage;
      const responseArtifacts = getProjectArtifacts(response.project ?? project ?? null);
      const nextActions = getLatestNextActionsForStage(responseArtifacts, activeStage);
      const draftArtifact = showDraftReview
        ? getStageWriteupArtifact(responseArtifacts, activeStage)
        : null;
      const showDraftChoice = !showDraftReview && isDraftOfferMessage(assistantContent) && activeStage >= 2 && activeStage <= 6;
      const responseClarification = (
        response.clarification &&
        Array.isArray(response.clarification.options) &&
        response.clarification.options.length > 0
      ) ? response.clarification : undefined;
      const displayAssistantContent = sanitizeClarificationMessageContent(assistantContent, responseClarification);
      const showStageAdvanceConsent = !responseClarification && isStageAdvanceConsentPrompt(displayAssistantContent);
      const stageAdvanceTarget = showStageAdvanceConsent ? getStageAdvanceTarget(displayAssistantContent) : undefined;

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: displayAssistantContent,
        timestamp: new Date(),
        topicCandidates: shouldShowCandidates ? responseTopicCandidates : undefined,
        clarification: responseClarification,
        formFields: formFields && formFields.length ? formFields : undefined,
        showStage2Form: showStage2Form || undefined,
        showDraftReview: showDraftReview || undefined,
        showDraftChoice: showDraftChoice || undefined,
        showStageAdvanceConsent: showStageAdvanceConsent || undefined,
        stageAdvanceTarget,
        draftChoiceStage: showDraftChoice ? activeStage : undefined,
        draftArtifact: draftArtifact || undefined,
        nextActions: nextActions && nextActions.length ? nextActions : undefined,
      };

      setChatMessages(prev => [...prev, assistantMessage]);
      setStreamingContent('');

      if (showDraftReview && draftArtifact) {
        setTimeout(() => setDraftModalState({ artifact: draftArtifact, stage: activeStage, initialMode: 'review' }), 400);
      }

      // Refresh project data to get any updates (stage changes, artifacts, etc.)
      if (response.project) {
        commitProjectSnapshot(response.project);
        scheduleWorkspaceRefresh();
      } else {
        await loadProject({ silent: true });
      }
    } catch (err) {
      console.error('Failed to send project message:', err);
      // Add error message
      setChatMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, there was an error processing your message. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleUploadProjectAttachment = async (captionText?: string) => {
    if (!project || !fileToUpload || isUploadingFile || isSending) return;

    const file = fileToUpload;
    const fileType = inferUploadFileType(file);
    const caption = String(captionText || '').trim();
    if ((fileType === 'DOCUMENT' || fileType === 'FILE') && !caption) {
      toast.error(`Please add a short description for this ${fileType.toLowerCase()} upload.`);
      return;
    }

    setIsUploadingFile(true);
    try {
      const response = await projectService.uploadMedia(
        projectId,
        file,
        fileType,
        caption || undefined,
        undefined,
        true
      );
      setInputText('');
      setFileToUpload(null);
      if (response.project) {
        commitProjectSnapshot(response.project);
      }
      await Promise.all([loadChatHistory(), loadProject({ silent: true })]);
      scheduleWorkspaceRefresh({ refreshMessages: true });
    } catch (err) {
      console.error('Failed to upload project file:', err);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleComposerSubmit = async () => {
    if (isComposerLocked) return;
    if (isSending || isUploadingFile) return;
    if (fileToUpload) {
      await handleUploadProjectAttachment(inputText);
      return;
    }
    await handleSendMessage();
  };

  const handleProjectFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    setFileToUpload(file);
  };

  const handleSelectTopicCandidate = (candidate: TopicCandidate) => {
    if (!candidate?.title) return;
    // Clicking a suggestion should fill the input (not auto-send), so the learner can edit.
    setInputText(candidate.title);
    inputRef.current?.focus();
  };

  const handleSelectClarificationOption = (optionId: string) => {
    if (!optionId) return;
    setIsClarificationOtherEnabled(false);
    void handleSendMessage(String(optionId));
  };

  const handleEnableClarificationOther = () => {
    setIsClarificationOtherEnabled(true);
    inputRef.current?.focus();
  };

  const handleProceedWithStageAdvance = (nextStage?: number) => {
    const stage = Number(nextStage || 0);
    const canonicalReply = stage > 0
      ? `Yes, please proceed to Stage ${stage}.`
      : 'Yes, please proceed to the next stage.';
    void handleSendMessage(canonicalReply);
  };

  const handleStageAdvanceFeedback = (feedback: string) => {
    const text = String(feedback || '').trim();
    if (!text) return;
    void handleSendMessage(`Request edit before stage transition: ${text}`);
  };

  const handleSubmitStage1Form = (answers: Record<string, string>) => {
    const lines: string[] = [];
    const where = answers.where?.trim();
    const what = answers.what?.trim();
    const who = answers.who_affected?.trim();
    const evidence = answers.evidence?.trim();
    const constraints = answers.constraints?.trim();
    const budget = answers.budget?.trim();
    const timeLimit = answers.time?.trim();
    const permissions = answers.permissions?.trim();
    const safety = answers.safety?.trim();
    const materials = answers.materials?.trim();

    if (what) lines.push(`What: ${what}`);
    if (where) lines.push(`Where: ${where}`);
    if (who) lines.push(`Who affected: ${who}`);
    if (evidence) lines.push(`Evidence: ${evidence}`);
    if (constraints) lines.push(`Constraints: ${constraints}`);
    if (budget) lines.push(`Budget: ${budget}`);
    if (timeLimit) lines.push(`Time: ${timeLimit}`);
    if (permissions) lines.push(`Permissions: ${permissions}`);
    if (safety) lines.push(`Safety: ${safety}`);
    if (materials) lines.push(`Materials: ${materials}`);

    if (!constraints) {
      const compactConstraintParts = [
        budget ? `budget=${budget}` : '',
        timeLimit ? `time=${timeLimit}` : '',
        permissions ? `permissions=${permissions}` : '',
        safety ? `safety=${safety}` : '',
        materials ? `materials=${materials}` : '',
      ].filter(Boolean);
      if (compactConstraintParts.length) {
        lines.push(`Constraints: ${compactConstraintParts.join('; ')}`);
      }
    }

    const payload = lines.join('\n');
    if (payload) {
      void handleSendMessage(payload);
    }
  };

  const handleSubmitStage2Form = (rows: Stage2QuickRow[]) => {
    const payload = buildStage2QuickTableMarkdown(rows);
    if (payload) {
      void handleSendMessage(payload);
    }
  };

  const handleDraftApprove = () => {
    void handleSendMessage('Looks good, proceed to the next stage.');
  };

  const handleDraftEdit = (editRequest: string) => {
    void handleSendMessage(editRequest);
  };

  const handleLetAIDraft = () => {
    void handleSendMessage('Yes, please draft it for me.');
  };

  const handleDoItMyself = () => {
    void handleSendMessage('I\'d like to write this stage myself. Please provide me with templates and guidance instead.');
  };

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isComposerLocked) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleComposerSubmit();
    }
  };

  // Delete a message from the chat
  const handleDeleteMessage = async (messageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await projectService.deleteMessage(messageId);
      // Remove message from local state
      setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
      toast.success('Message deleted');
    } catch (err) {
      console.error('Failed to delete message:', err);
      toast.error('Failed to delete message');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--accent-primary)]" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-3xl mx-auto p-6 sm:p-8">
        <div className="mb-6 flex items-center gap-2">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          {onExitToChat && (
            <button
              type="button"
              onClick={onExitToChat}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
            >
              Back to Chat
            </button>
          )}
        </div>
        <div className="p-6 rounded-2xl border border-red-500/20 bg-red-500/10 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-sm text-red-400">{error || 'Project not found'}</p>
        </div>
      </div>
    );
  }

  const subtitle = [
    project.subject_id && project.subject_id !== 'general'
      ? baseSubjectNameFromId(project.subject_id)
      : null,
  ]
    .filter(Boolean)
    .join(' • ');
  const projectArtifacts = getProjectArtifacts(project);
  const sidebarNextActions = getLatestNextActionsForStage(projectArtifacts, project.current_stage) || [];
  const activeProjectStage = Number(project.current_stage || 1);
  const stickyDraftArtifact = getStageWriteupArtifact(projectArtifacts, activeProjectStage);
  const shouldShowStickyDraftBar = [2, 3, 4, 5, 6].includes(activeProjectStage) && Boolean(stickyDraftArtifact);
  const fullProjectDraftArtifact = getFullProjectDraftArtifact(project);
  const composerPlaceholder = isClarificationComposerLocked
    ? 'Choose one option above to continue.'
    : isStageConsentComposerLocked
      ? 'Use the stage decision buttons above to continue.'
    : activeClarificationMessageId && isClarificationOtherEnabled
      ? 'Type your own answer (or click an option above)...'
      : 'Ask about your project, request guidance, or share your progress...';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
        <div className="flex items-center justify-between gap-4 max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            {onExitToChat && (
              <button
                type="button"
                onClick={onExitToChat}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
              >
                Back to Chat
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center">
                <FolderKanban className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                  {project.title || 'Untitled Project'}
                </h2>
                <p className="text-xs text-[var(--text-secondary)]">{subtitle || 'Project workspace'}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setDraftUpdatedNotice(false);
                if (fullProjectDraftArtifact) {
                  setFullDraftModalArtifact(fullProjectDraftArtifact);
                }
              }}
              className="px-3 py-1.5 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] text-xs text-[var(--text-primary)] hover:border-cyan-500/40 hover:text-cyan-200 transition-colors"
              disabled={!fullProjectDraftArtifact}
            >
              View full draft
            </button>
            {draftUpdatedNotice && (
              <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-200">
                Draft updated
              </span>
            )}
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                project.status === 'COMPLETED'
                  ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                  : project.status === 'SUBMITTED_FOR_GRADING'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/20'
                  : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/20'
              }`}
            >
              Stage {project.current_stage}: {STAGE_NAMES[project.current_stage]}
            </span>
          </div>
        </div>
      </div>

      {/* Main content area - split between chat and info panels */}
      <div className="flex-1 overflow-hidden flex">
        {/* Chat area - main column */}
        <div className={`flex-1 flex flex-col min-w-0 ${chatMessages.length === 0 && !streamingContent ? 'justify-center' : ''}`}>
          {/* Messages area */}
          <div ref={messagesAreaRef} className={`overflow-y-auto overflow-x-hidden p-4 ${chatMessages.length > 0 || streamingContent ? 'flex-1' : ''}`}>
            <div className="max-w-3xl mx-auto space-y-4">
            {chatMessages.length === 0 && !streamingContent ? (
              <div className="flex flex-col items-center text-center px-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center mb-3">
                  <MessageCircle className="w-7 h-7 text-cyan-300" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                  Project Coaching
                </h3>
              </div>
            ) : (
              <>
                {chatMessages.map((msg) => {
                  const messageMedia = extractProjectMessageMedia(msg.metadata);
                  const messageMediaUrl = messageMedia ? mediaObjectUrls[messageMedia.mediaId] : undefined;
                  const clarificationOptions = msg.clarification?.options ?? [];
                  const hasClarificationOptions =
                    msg.role === 'assistant' &&
                    clarificationOptions.length > 0;
                  const clarificationTitle = String(
                    msg.clarification?.clarification_title || msg.clarification?.question || ''
                  ).trim();
                  const clarificationSubtitle = String(msg.clarification?.clarification_subtitle || '').trim();
                  const clarificationOtherOptionLabel =
                    String(msg.clarification?.other_option_label || '').trim() || 'Other (type my own answer)';
                  const wrapperCandidates = new Set(
                    [
                      clarificationTitle,
                      clarificationSubtitle,
                      `${clarificationTitle} ${clarificationSubtitle}`.trim(),
                      String(msg.clarification?.question || '').trim(),
                    ]
                      .map((value) => value.toLowerCase())
                      .filter(Boolean)
                  );
                  const normalizedContent = String(msg.content || '').trim();
                  const shouldSuppressContent =
                    hasClarificationOptions && wrapperCandidates.has(normalizedContent.toLowerCase());
                  const contentToRender = shouldSuppressContent ? '' : normalizedContent;
                  return (
                  <div
                    key={msg.id}
                    className={`group flex items-start gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* Delete button for user messages - left side */}
                    {msg.role === 'user' && !msg.id.startsWith('error-') && (
                      <button
                        onClick={(e) => handleDeleteMessage(msg.id, e)}
                        className="
                          flex-shrink-0 self-center
                          opacity-0 group-hover:opacity-100
                          p-1.5 rounded-lg
                          bg-red-500/10 hover:bg-red-500/20
                          border border-red-400/20 hover:border-red-400/40
                          text-red-400 hover:text-red-300
                          transition-all duration-200
                          backdrop-blur-sm
                        "
                        title="Delete message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <div
                      className={`max-w-[75%] sm:max-w-[70%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)]'
                      }`}
                    >
                      {messageMedia && (
                        <ProjectMediaAttachment media={messageMedia} mediaUrl={messageMediaUrl} />
                      )}
                      {contentToRender && (
                        <p className="text-sm whitespace-pre-wrap break-words">{contentToRender}</p>
                      )}
                      {hasClarificationOptions && (
                        <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
                          <div className="rounded-xl border border-cyan-400/35 bg-cyan-500/5 px-2.5 py-2.5">
                            <div className="mb-2 flex items-center gap-2">
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" />
                              <span className="text-[10px] uppercase tracking-wide text-cyan-200">Select one</span>
                            </div>
                            {(clarificationTitle || clarificationSubtitle) && (
                              <div className="mb-2.5 space-y-1">
                                {clarificationTitle && (
                                  <p className="text-xs font-medium text-cyan-100">{clarificationTitle}</p>
                                )}
                                {clarificationSubtitle && (
                                  <p className="text-[11px] leading-snug text-[var(--text-secondary)]">
                                    {clarificationSubtitle}
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="space-y-2">
                              {clarificationOptions.slice(0, 3).map((opt, idx) => {
                                const optionId = String(opt.id || idx + 1);
                                const title = String(opt.title || `Option ${idx + 1}`);
                                const subtitle = String(opt.subtitle || '').trim();
                                return (
                                  <button
                                    key={`${optionId}-${idx}`}
                                    type="button"
                                    className="w-full text-left px-3 py-2 rounded-lg border border-cyan-400/40 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20 hover:-translate-y-0.5 transition-all"
                                    disabled={isSending}
                                    onClick={() => handleSelectClarificationOption(optionId)}
                                  >
                                    <div className="text-xs font-medium text-cyan-200">{title}</div>
                                    {subtitle && (
                                      <p className="mt-1 text-[11px] text-[var(--text-secondary)] leading-snug">{subtitle}</p>
                                    )}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]/70 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-cyan-400/40 hover:-translate-y-0.5 transition-all"
                                disabled={isSending}
                                onClick={handleEnableClarificationOther}
                              >
                                <div className="text-xs font-medium">{clarificationOtherOptionLabel}</div>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {msg.role === 'assistant' && msg.showStageAdvanceConsent && msg.id === activeStageConsentMessageId && (
                        <StageAdvanceConsentCard
                          nextStage={msg.stageAdvanceTarget}
                          disabled={isSending}
                          onProceed={() => handleProceedWithStageAdvance(msg.stageAdvanceTarget)}
                          onSendFeedback={handleStageAdvanceFeedback}
                        />
                      )}
                      {msg.role === 'assistant' && msg.topicCandidates && msg.topicCandidates.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
                          <TopicCandidates candidates={msg.topicCandidates} onSelectCandidate={handleSelectTopicCandidate} />
                          <p className="mt-2 text-[10px] text-[var(--text-tertiary)]">
                            Click a suggestion to copy it into the message box below.
                          </p>
                        </div>
                      )}
                      {msg.role === 'assistant'
                        && msg.formFields
                        && msg.formFields.length > 0
                        && shouldShowStage1Form(msg.content, project ?? null) && (
                        <Stage1FormBubble
                          fields={msg.formFields}
                          disabled={isSending}
                          onSubmit={handleSubmitStage1Form}
                        />
                      )}
                      {msg.role === 'assistant' && msg.showStage2Form && shouldShowStage2Form(project ?? null) && (
                        <Stage2FormBubble
                          disabled={isSending}
                          onSubmit={handleSubmitStage2Form}
                        />
                      )}
                      {msg.role === 'assistant' && msg.showDraftReview && shouldShowDraftReview(project ?? null) && (
                        <DraftReviewCard
                          stage={Number(project?.current_stage || 2)}
                          artifact={getStageWriteupArtifact(getProjectArtifacts(project ?? null), Number(project?.current_stage || 2)) || msg.draftArtifact}
                          disabled={isSending}
                          onApprove={handleDraftApprove}
                          onEdit={handleDraftEdit}
                        />
                      )}
                      {msg.role === 'assistant'
                        && msg.showDraftChoice
                        && Number(project?.current_stage || 0) >= 2
                        && Number(project?.current_stage || 0) <= 6
                        && (!msg.draftChoiceStage || msg.draftChoiceStage === Number(project?.current_stage || 0)) && (
                        <DraftChoiceCard
                          stage={msg.draftChoiceStage || Number(project?.current_stage || 2)}
                          disabled={isSending}
                          onLetAIDraft={handleLetAIDraft}
                          onDoItMyself={handleDoItMyself}
                        />
                      )}
                      <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-white/60' : 'text-[var(--text-tertiary)]'}`}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {/* Delete button for assistant messages - right side */}
                    {msg.role !== 'user' && !msg.id.startsWith('error-') && (
                      <button
                        onClick={(e) => handleDeleteMessage(msg.id, e)}
                        className="
                          flex-shrink-0 self-center
                          opacity-0 group-hover:opacity-100
                          p-1.5 rounded-lg
                          bg-red-500/10 hover:bg-red-500/20
                          border border-red-400/20 hover:border-red-400/40
                          text-red-400 hover:text-red-300
                          transition-all duration-200
                          backdrop-blur-sm
                        "
                        title="Delete message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
                })}
                {isSending && !streamingContent && (
                  <ThinkingBubble />
                )}
                {streamingContent && (
                  <div className="flex justify-start">
                    <div className="max-w-[75%] sm:max-w-[70%] rounded-2xl px-4 py-3 bg-[var(--surface-elevated)] border border-[var(--border-color)]">
                      <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap break-words">{streamingContent}</p>
                      <div className="flex items-center gap-1.5 mt-2" role="status" aria-label="AI is writing">
                        <span className="typing-dot typing-dot-1 inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
                        <span className="typing-dot typing-dot-2 inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
                        <span className="typing-dot typing-dot-3 inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
            </div>
          </div>

          {/* Input area */}
          <div className={`flex-shrink-0 p-4 bg-[var(--bg-primary)] ${chatMessages.length > 0 || streamingContent ? 'border-t border-[var(--border-color)]' : ''}`}>
            <div className={chatMessages.length === 0 && !streamingContent ? 'max-w-xl mx-auto' : ''}>
            {shouldShowStickyDraftBar && stickyDraftArtifact && (
              <StickyDraftActionsBar
                stage={activeProjectStage}
                artifact={stickyDraftArtifact}
                disabled={isSending}
                onReview={() => setDraftModalState({ artifact: stickyDraftArtifact, stage: activeProjectStage, initialMode: 'review' })}
                onApprove={handleDraftApprove}
                onEdit={() => setDraftModalState({ artifact: stickyDraftArtifact, stage: activeProjectStage, initialMode: 'editing' })}
              />
            )}
            {isClarificationComposerLocked && (
              <div className="mb-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 flex items-center justify-between gap-3">
                <p className="text-xs text-cyan-100">Choose one option above to continue.</p>
                <button
                  type="button"
                  onClick={handleEnableClarificationOther}
                  className="px-2.5 py-1 rounded-md border border-cyan-400/40 text-[11px] text-cyan-200 hover:bg-cyan-500/20 transition-colors"
                  disabled={isSending}
                >
                  Other
                </button>
              </div>
            )}
            {!isClarificationComposerLocked && isStageConsentComposerLocked && (
              <div className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                <p className="text-xs text-emerald-100">
                  Use the proceed/feedback buttons above to confirm before moving to the next stage.
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={PROJECT_UPLOAD_ACCEPTED_TYPES}
              onChange={handleProjectFilePick}
            />
            {fileToUpload && (
              <div className="mb-2 rounded-lg border border-[var(--border-color)]/70 bg-[var(--bg-secondary)]/40 px-3 py-2 flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-[var(--text-primary)] truncate">{fileToUpload.name}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">
                    {inferUploadFileType(fileToUpload).toLowerCase()} • {(fileToUpload.size / (1024 * 1024)).toFixed(1)}MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFileToUpload(null)}
                  className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  disabled={isUploadingFile}
                  aria-label="Remove selected file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={composerPlaceholder}
                rows={2}
                className="flex-1 px-4 py-3 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-cyan-500/50 resize-none"
                disabled={isSending || isUploadingFile || isComposerLocked}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending || isUploadingFile || isComposerLocked}
                className="px-3 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-cyan-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Attach image, audio, video, or file"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <button
                onClick={(_e) => {
                  void handleComposerSubmit();
                }}
                disabled={(!inputText.trim() && !fileToUpload) || isSending || isUploadingFile || isComposerLocked}
                className="px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending || isUploadingFile ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Info sidebar - right column */}
        <div className="w-80 flex-shrink-0 border-l border-[var(--border-color)] overflow-y-auto p-4 space-y-4 hidden lg:block">
          {/* Stage timeline */}
          <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Progress</h3>
            <StageTimeline currentStage={project.current_stage} stageStatus={project.stage_status || {}} />
          </div>

          {/* Full draft quick access */}
          <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[var(--accent-primary)]" />
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Full project draft</h3>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-3">
              Open the compiled draft anytime. Sections with placeholders are highlighted for completion.
            </p>
            <button
              type="button"
              onClick={() => {
                setDraftUpdatedNotice(false);
                if (fullProjectDraftArtifact) {
                  setFullDraftModalArtifact(fullProjectDraftArtifact);
                }
              }}
              className="w-full px-3 py-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-200 text-xs font-medium hover:bg-cyan-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!fullProjectDraftArtifact}
            >
              Open full draft
            </button>
          </div>

          {/* Problem statement */}
          {project.problem_statement && (
            <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Problem Statement</h3>
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{project.problem_statement}</p>
            </div>
          )}

          {/* Next actions */}
          <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-[var(--accent-primary)]" />
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Next actions</h3>
            </div>
            {sidebarNextActions.length > 0 ? (
              <NextActionsChecklist actions={sidebarNextActions} compact />
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">No checklist yet. Ask for the next steps for this stage.</p>
            )}
          </div>

          {/* Artifacts */}
          <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[var(--accent-primary)]" />
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Artifacts</h3>
            </div>
            <ArtifactsPanel artifacts={projectArtifacts} />
          </div>

          {/* Sources */}
          {project.sources && project.sources.length > 0 && (
            <div className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)]">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-[var(--accent-primary)]" />
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">Sources</h3>
              </div>
              <p className="text-[11px] text-[var(--text-secondary)] mb-3">
                Internet-derived references used by the coach are listed here with links.
              </p>
              <div className="space-y-2">
                {project.sources.map((source) => (
                  <a
                    key={source.id}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-[var(--accent-primary)] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs font-medium text-[var(--accent-primary)] truncate">
                        {source.title || source.url}
                      </div>
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-cyan-500/30 text-cyan-300">
                        Web
                      </span>
                    </div>
                    {source.snippet && (
                      <p className="mt-1 text-[11px] text-[var(--text-secondary)] line-clamp-2">{source.snippet}</p>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {draftModalState && (
        <DraftReviewModal
          artifact={draftModalState.artifact}
          stage={draftModalState.stage}
          initialMode={draftModalState.initialMode}
          onClose={() => setDraftModalState(null)}
          onApprove={() => {
            handleDraftApprove();
            setDraftModalState(null);
          }}
          onEdit={(text) => {
            handleDraftEdit(text);
            setDraftModalState(null);
          }}
          disabled={isSending}
        />
      )}
      {fullDraftModalArtifact && (
        <FullProjectDraftModal
          artifact={fullDraftModalArtifact}
          onClose={() => setFullDraftModalArtifact(null)}
        />
      )}
    </div>
  );
};
